// =============================================================================
// Catálogo de ofertas — montagem
//
// O catálogo é uma lista de PÁGINAS, e cada página é uma pilha de BLOCOS. Cada
// bloco tem um estilo (com moldura, soltos, faixa amarela, quadro branco),
// quantos produtos por linha e quantas linhas — assim uma página pode ter faixa
// amarela em cima e quadro branco embaixo, como no catálogo de 2025. A exceção
// é a capa da arte: os 15 quadros fixos desenhados pelo designer.
//
// Cada produto mora num lugar: a gerente arrasta da lista pro ponto exato, ou
// clica no + e ele vai pra próxima vaga livre. A prévia usa a mesma conta de
// geometria do PDF (helpers/catalogoGeometria.js), então o que ela vê é o que
// sai impresso.
// =============================================================================
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import '@fontsource/poppins/400.css';
import '@fontsource/poppins/400-italic.css';
import '@fontsource/poppins/700.css';
import '@fontsource/poppins/800.css';
import { API } from '../contexts/AuthContext';
import { prepararFoto, reduzirParaEnvio, estiloPadrao, ESTILOS, carregarImagem } from '../helpers/fotoCatalogo';
import { layoutPagina, cabeNoBloco, encaixarBloco, alturaLinhaPagina, folhaAjustada, tarjaAjustada, medidasCard,
  caixaLivre, gradeLivre, vagaLivre, areaLivre } from '../helpers/catalogoGeometria';
import AvisoSugestaoFoto from '../components/AvisoSugestaoFoto';
import AvisoRecorteIA from '../components/AvisoRecorteIA';
import { esquecerFoto } from '../helpers/recorteIA';
import { useUsoPecas, estiloLinhaUsada, MarcasOutrasPecas, LegendaUso } from '../components/UsoPecas';

const fmt = (v) => Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const milhar = (s) => String(s).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
const UNIDADES = { M2: 'm²', SC: 'sc', PCT: 'pct', ML: 'm', KG: 'kg' };
const OURO = '#F7BB2D';
const uid = () => Math.random().toString(36).slice(2, 10);

const ESTILOS_BLOCO = [
  { valor: 'quadros', rotulo: 'Com moldura' },
  { valor: 'soltos', rotulo: 'Soltos' },
  { valor: 'amarela', rotulo: 'Faixa amarela' },
  { valor: 'branca', rotulo: 'Quadro branco' },
];
const CAPACIDADE_ARTE = 15;
// Zoom das páginas (do mais perto pro mais longe).
const ZOOMS = [
  { v: 'largura', rotulo: 'Largura toda' },
  { v: 'inteira', rotulo: 'Página inteira' },
  { v: 2, rotulo: '2 por linha' },
  { v: 3, rotulo: '3 por linha' },
  { v: 4, rotulo: '4 por linha' },
  { v: 6, rotulo: '6 por linha' },
];

// Foto nova: piso entra em "dois pisos", o resto recortado.
// `ver` sobe a cada mudança na foto: resultado de tratamento antigo é descartado.
const fotoPadrao = (p) => ({ origem: p?.fotoPropria ? 'propria' : 'erp', estilo: estiloPadrao(p?.classe),
  tocouEstilo: false, cobre: false, zoom: 1, x: 0, y: 0, url: null, carregando: true, ver: 0 });
// `tamanho`: células do card (normal, largo 2x1, alto 1x2, grande 2x2). `folha`
// e `tarja`: onde a gerente deixou a folha do preço e a fita do "De:" dentro do
// card (null = padrão). Contas em helpers/catalogoGeometria.js.
// `caixa`: onde o produto fica numa página livre (% da página); `mostrarDe`: a
// fita do "De:" (ligada por padrão, como o catálogo sempre saiu).
const novoItem = (p) => ({ ...p, grande: false, tamanho: 'normal', folha: null, tarja: null, caixa: null, mostrarDe: true, foto: fotoPadrao(p) });
const tamanhoDe = (it) => it.tamanho || (it.grande ? 'grande' : 'normal');
const TAMANHOS_CARD = [
  { valor: 'normal', rotulo: 'Normal', ajuda: '1 quadro' },
  { valor: 'largo', rotulo: 'Largo', ajuda: '2 quadros lado a lado' },
  { valor: 'alto', rotulo: 'Alto', ajuda: '2 quadros um sobre o outro' },
  { valor: 'grande', rotulo: 'Grande', ajuda: '2 x 2 quadros' },
];
const novoBloco = (base = {}) => ({ id: uid(), estilo: base.estilo || 'quadros',
  cols: base.cols || 4, linhas: base.linhas || 5, itens: [] });
const novaPagina = (modelo) => ({ id: uid(),
  blocos: modelo?.blocos?.length ? modelo.blocos.map(b => novoBloco(b)) : [novoBloco()] });
const capaArte = (itens = []) => ({ id: uid(), arte: true, itens });
// Página livre: cada produto na caixa dele (arrasta e muda o tamanho, como no story).
// Nova nasce sem moldura (só foto e preço, como no story); a moldura é opção da página.
const paginaLivre = (itens = [], estilo = 'soltos') => ({ id: uid(), livre: true, estilo, itens });
const tipoPagina = (pg) => (pg.arte ? 'arte' : pg.livre ? 'livre' : 'grade');

// ---- Operações sobre a estrutura (sempre devolvem uma cópia nova) ----------
const todosItens = (paginas) => paginas.flatMap(pg => (pg.arte || pg.livre ? pg.itens : pg.blocos.flatMap(b => b.itens)));

function mapItens(paginas, fn) {
  return paginas.map(pg => (pg.arte || pg.livre
    ? { ...pg, itens: fn(pg.itens, { pagina: pg.id }) }
    : { ...pg, blocos: pg.blocos.map(b => ({ ...b, itens: fn(b.itens, { pagina: pg.id, bloco: b.id }) })) }));
}
const mudarItemEm = (paginas, codpro, fn) =>
  mapItens(paginas, itens => itens.map(it => (it.codpro === codpro ? fn(it) : it)));

function retirar(paginas, codpro) {
  let item = null, origem = null;
  const novas = mapItens(paginas, (itens, onde) => {
    const i = itens.findIndex(it => it.codpro === codpro);
    if (i < 0) return itens;
    item = itens[i]; origem = { ...onde, pos: i };
    return itens.filter((_, k) => k !== i);
  });
  return [novas, item, origem];
}

function inserir(paginas, destino, pos, item) {
  return mapItens(paginas, (itens, onde) => {
    if (onde.pagina !== destino.pagina || (onde.bloco || null) !== (destino.bloco || null)) return itens;
    const copia = [...itens];
    copia.splice(pos == null ? copia.length : Math.min(pos, copia.length), 0, item);
    return copia;
  });
}

// Próxima vaga livre, na ordem das páginas (na página livre, o primeiro canto
// da grade padrão que não bate em ninguém). Sem vaga, abre uma página nova do
// mesmo jeito da última.
function colocarNoProximo(paginas, item, G) {
  const tipoDe = (n) => (n === 0 ? 'capa' : 'interna');
  for (let n = 0; n < paginas.length; n++) {
    const pg = paginas[n];
    if (pg.arte && pg.itens.length < CAPACIDADE_ARTE) return inserir(paginas, { pagina: pg.id }, null, item);
    if (pg.livre) {
      const vaga = vagaLivre(G, tipoDe(n), pg.itens.map(i => i.caixa));
      if (vaga) return inserir(paginas, { pagina: pg.id }, null, { ...item, caixa: vaga });
    } else if (!pg.arte) {
      const b = pg.blocos.find(bl => cabeNoBloco(bl, item));
      if (b) return inserir(paginas, { pagina: pg.id, bloco: b.id }, null, item);
    }
  }
  const modelo = [...paginas].reverse().find(pg => !pg.arte);
  if (modelo?.livre) {
    const nova = paginaLivre([], modelo.estilo);
    return [...paginas, { ...nova, itens: [{ ...item, caixa: vagaLivre(G, tipoDe(paginas.length), []) }] }];
  }
  const nova = novaPagina(modelo);
  return inserir([...paginas, nova], { pagina: nova.id, bloco: nova.blocos[0].id }, null, item);
}

// ---- Salvar / carregar -----------------------------------------------------
const itemParaSalvar = (i) => ({ codpro: i.codpro, nome: i.nome, grande: tamanhoDe(i) === 'grande', tamanho: tamanhoDe(i),
  folha: i.folha || null, tarja: i.tarja || null, caixa: i.caixa || null, mostrarDe: i.mostrarDe !== false,
  foto: { origem: i.foto.origem, estilo: i.foto.estilo, tocouEstilo: i.foto.tocouEstilo,
    zoom: i.foto.zoom, x: i.foto.x, y: i.foto.y } });

// O que conta como mudança não salva num item (o estilo que o recorte sugeriu sozinho não conta).
const itemAssinado = (i) => {
  const x = itemParaSalvar(i);
  return { ...x, foto: { ...x.foto, estilo: x.foto.tocouEstilo ? x.foto.estilo : '' } };
};

function restaurar(salvo, porCod) {
  const item = (s) => {
    const p = porCod.get(s.codpro);
    if (!p) return null;
    return { ...p, nome: s.nome || p.nome, grande: !!s.grande, tamanho: s.tamanho || (s.grande ? 'grande' : 'normal'),
      folha: s.folha || null, tarja: s.tarja || null, caixa: s.caixa || null, mostrarDe: s.mostrarDe !== false,
      foto: { ...fotoPadrao(p), ...s.foto, url: null, bruta: null, carregando: true } };
  };
  const lista = (arr) => (arr || []).map(item).filter(Boolean);

  if (salvo?.versao === 2 && Array.isArray(salvo.paginas)) {
    const pags = salvo.paginas.map(pg => (pg.arte
      ? capaArte(lista(pg.itens))
      : pg.livre ? paginaLivre(lista(pg.itens), pg.estilo === 'soltos' ? 'soltos' : 'quadros')
        : { id: uid(), blocos: (pg.blocos || []).map(b => ({ ...novoBloco(b), itens: lista(b.itens) })) }));
    return pags.length ? pags : [capaArte()];
  }

  // Formato antigo (lista corrida): capa da arte + páginas de 4x5.
  const itens = lista(salvo?.itens);
  const comArte = !(salvo?.modo === 'livre' || salvo?.paginas?.[0]?.modelo === 'quadros');
  const pags = [];
  let resto = itens;
  if (comArte) { pags.push(capaArte(resto.slice(0, CAPACIDADE_ARTE))); resto = resto.slice(CAPACIDADE_ARTE); }
  while (resto.length) {
    const pg = novaPagina();
    const { postos, sobra } = encaixarBloco(resto, pg.blocos[0].cols, pg.blocos[0].linhas);
    if (!postos.length) break;
    pg.blocos[0].itens = postos.map(x => x.p);
    pags.push(pg);
    resto = sobra;
  }
  return pags.length ? pags : [capaArte()];
}

// `embutido`: aba do Estúdio de Criação (pages/EstudioCampanha.js) — sem o
// cabeçalho próprio; as páginas usam a identidade da campanha (`versaoArte` muda
// quando a aba Identidade troca a arte) e a tela conta pra aba quantos produtos
// tem e se há mudança não salva (`onResumo`).
export default function CampanhaCatalogo({ embutido = false, campanhaId, versaoArte = 0, onResumo } = {}) {
  const { id: idRota } = useParams();
  const id = String(campanhaId || idRota);
  const navigate = useNavigate();

  const [campanha, setCampanha] = useState(null);
  const [grade, setGrade] = useState(null);
  const [disponiveis, setDisponiveis] = useState([]);
  const [paginas, setPaginas] = useState([capaArte()]);
  const [busca, setBusca] = useState('');
  const [validade, setValidade] = useState('');
  const [carregando, setCarregando] = useState(true);
  const [gerando, setGerando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [editando, setEditando] = useState(null);       // codpro aberto no editor
  const [arrastandoTipo, setArrastandoTipo] = useState(null); // 'novo' | 'item' | null
  const [sobreLista, setSobreLista] = useState(false);
  const [sobreLixeira, setSobreLixeira] = useState(false);
  const [previa, setPrevia] = useState(null);
  // O que está sendo arrastado: { tipo: 'novo', produto } ou { tipo: 'item', codpro }.
  const arrastando = useRef(null);
  const uso = useUsoPecas(id);
  // Zoom das páginas: 'inteira' (a página cabe na altura), 'largura' (largura toda)
  // ou N páginas por linha pra ver o catálogo todo. Fica lembrado neste computador.
  const [zoom, setZoom] = useState(() => {
    try { const z = JSON.parse(localStorage.getItem('catalogo-zoom')); return ZOOMS.some(o => o.v === z) ? z : 'inteira'; }
    catch { return 'inteira'; }
  });
  useEffect(() => { try { localStorage.setItem('catalogo-zoom', JSON.stringify(zoom)); } catch { /* sem localStorage */ } }, [zoom]);
  const colPaginas = useRef(null);
  const [tamCol, setTamCol] = useState({ l: 800, a: 700 });
  const montado = useRef(true);
  const urlsBrutas = useRef(new Set());   // fotos baixadas (object URLs), pra liberar

  const itens = useMemo(() => todosItens(paginas), [paginas]);

  // ---- Identidade da campanha nas páginas (o mesmo que o PDF usa: routes/catalogo.js
  // › visualCatalogo). Por página: { arte } (página inteira) ou { fundo, topo }.
  const [visual, setVisual] = useState(null);
  const visualAtual = useRef(null);
  const soltarVisual = (v) => new Set(Object.values(v || {}).flatMap(x => Object.values(x || {}))).forEach(u => u && URL.revokeObjectURL(u));
  const carregarVisual = useCallback(async () => {
    const C = `${API}/catalogo/campanha/${id}/visual`;
    const baixar = async (u) => {
      try { const r = await axios.get(u, { responseType: 'blob' }); return r.status === 204 || !r.data?.size ? null : URL.createObjectURL(r.data); }
      catch { return null; }
    };
    const composto = async (pag) => ({ fundo: await baixar(`${C}/${pag}/fundo`), topo: await baixar(`${C}/${pag}/topo`) });
    const v = (await axios.get(C)).data;
    let capa, livre, miolo;
    if (v.capa === 'padrao') { capa = { arte: await baixar(`${C}/capa/arte`) }; livre = { arte: await baixar(`${C}/capa-livre/arte`) }; }
    else if (v.capa === 'arte') { capa = { arte: await baixar(`${C}/capa/arte`) }; livre = capa; }
    else { capa = await composto('capa'); livre = capa; }
    if (v.miolo === 'composto') miolo = v.capa === 'composto' ? capa : await composto('miolo');
    else miolo = { arte: await baixar(`${C}/miolo/arte`) };
    const novo = { capa, livre, miolo };
    soltarVisual(visualAtual.current);
    visualAtual.current = novo;
    setVisual(novo);
  }, [id]);
  useEffect(() => { carregarVisual().catch(() => {}); }, [carregarVisual, versaoArte]);
  useEffect(() => () => soltarVisual(visualAtual.current), []);

  // ---- Foto: busca com o token e trata no navegador (blob = mesma origem, sem
  // isso o canvas fica "contaminado" e o recorte não roda).
  const buscarFoto = useCallback(async (codpro, origem) => {
    const url = origem === 'propria'
      ? `${API}/catalogo/campanha/${id}/foto/${codpro}`
      : `${API}/produtos/${codpro}/imagem/1`;
    const r = await axios.get(url, { responseType: 'blob' });
    const u = URL.createObjectURL(r.data);
    urlsBrutas.current.add(u);
    return u;
  }, [id]);
  const soltarUrl = (u) => { if (u) { URL.revokeObjectURL(u); urlsBrutas.current.delete(u); esquecerFoto(u); } };

  const tratarFoto = useCallback(async (item) => {
    try {
      const bruta = item.foto.bruta || await buscarFoto(item.codpro, item.foto.origem);
      let estilo = item.foto.estilo || 'recorte';
      let r = await prepararFoto(bruta, { estilo });
      if (r.sugestao && !item.foto.tocouEstilo) {
        estilo = r.sugestao;
        r = await prepararFoto(bruta, { estilo });
      }
      return { ...item.foto, bruta, url: r.url, cobre: r.cobre, estilo, carregando: false, sincronizada: false };
    } catch {
      return { ...item.foto, url: null, carregando: false, erro: true };
    }
  }, [buscarFoto]);

  // Mudou a foto (estilo, origem): nova versão, volta pra fila e precisa subir de
  // novo pro servidor antes de gerar. Trocar a origem descarta a imagem baixada.
  const mudarFoto = useCallback((codpro, mudanca) => {
    setPaginas(prev => mudarItemEm(prev, codpro, it => {
      if ('bruta' in mudanca && it.foto.bruta) soltarUrl(it.foto.bruta);
      return { ...it, foto: { ...it.foto, ...mudanca, carregando: true, erro: false,
        sincronizada: false, ver: (it.foto.ver || 0) + 1 } };
    }));
  }, []);

  // ---- Carga inicial
  useEffect(() => {
    (async () => {
      try {
        const [g, p, c, l] = await Promise.all([
          axios.get(`${API}/catalogo/grade`),
          axios.get(`${API}/catalogo/campanha/${id}/produtos`),
          axios.get(`${API}/campanhas/${id}`),
          axios.get(`${API}/catalogo/campanha/${id}/layout`),
        ]);
        setGrade(g.data);
        setDisponiveis(p.data.produtos || []);
        setCampanha(c.data);
        if (c.data?.data_fim) setValidade(String(c.data.data_fim).slice(0, 10).split('-').reverse().slice(0, 2).join('/'));
        const salvo = l.data?.layout;
        if (salvo) {
          setPaginas(restaurar(salvo, new Map((p.data.produtos || []).map(x => [x.codpro, x]))));
          if (salvo.validade) setValidade(salvo.validade);
        }
      } catch (e) {
        Swal.fire('Erro', e.response?.data?.error || 'Não consegui carregar a campanha.', 'error');
      } finally {
        setCarregando(false);
      }
    })();
  }, [id]);

  // Fila de fotos (entrada nova, troca de estilo, montagem salva). Duas por vez.
  // O tratamento NÃO é reiniciado quando a tela re-renderiza — antes cada tecla
  // no texto ou passo do zoom recomeçava o mesmo download e recorte. Se a foto
  // mudou enquanto era tratada, o resultado velho é descartado pela versão.
  const emAndamento = useRef(new Set());
  const [fimDeTarefa, setFimDeTarefa] = useState(0);
  useEffect(() => {
    const livres = 2 - emAndamento.current.size;
    if (livres <= 0) return;
    itens
      .filter(it => it.foto.carregando && !it.foto.erro && !emAndamento.current.has(`${it.codpro}:${it.foto.ver}`))
      .slice(0, livres)
      .forEach(item => {
        const chave = `${item.codpro}:${item.foto.ver}`;
        emAndamento.current.add(chave);
        tratarFoto(item).then(foto => {
          emAndamento.current.delete(chave);
          if (!montado.current) return;
          setPaginas(prev => mudarItemEm(prev, item.codpro, it => (it.foto.ver === item.foto.ver
            ? { ...it, foto: { ...foto, ver: item.foto.ver } } : it)));
          setFimDeTarefa(n => n + 1);   // libera a vaga pra próxima da fila
        });
      });
  }, [itens, tratarFoto, fimDeTarefa]);

  // Ao sair da tela, devolve a memória das fotos baixadas. O "montado = true" no
  // corpo é pro modo de desenvolvimento, que monta, desmonta e monta de novo:
  // sem ele toda foto tratada era descartada e o spinner ficava pra sempre.
  useEffect(() => {
    const urls = urlsBrutas.current;
    montado.current = true;
    return () => {
      montado.current = false;
      urls.forEach(u => { URL.revokeObjectURL(u); esquecerFoto(u); });
      urls.clear();
    };
  }, []);

  // Mede a coluna das páginas (o zoom calcula a largura de cada página por ela).
  useEffect(() => {
    const el = colPaginas.current;
    if (!el) return undefined;
    const medir = () => setTamCol({ l: el.clientWidth, a: el.clientHeight });
    medir();
    const obs = new ResizeObserver(medir);
    obs.observe(el);
    return () => obs.disconnect();
  }, [carregando]);

  // ---- Geometria de todas as páginas (mesma conta do PDF)
  const layout = useMemo(() => {
    if (!grade) return [];
    const G = grade.geometria;
    return paginas.map((pg, n) => {
      if (pg.arte) {
        return { pg, n, tipo: 'arte',
          cards: pg.itens.slice(0, CAPACIDADE_ARTE).map((p, k) => ({ p, k, medidas: grade.capa[k].medidas })),
          sobra: pg.itens.slice(CAPACIDADE_ARTE) };
      }
      if (pg.livre) {
        // Produto sem caixa (veio de outra página) entra na grade padrão até ser arrastado.
        const padrao = gradeLivre(G, n === 0 ? 'capa' : 'interna', pg.itens.length);
        return { pg, n, tipo: 'livre', sobra: [],
          cards: pg.itens.map((p, k) => {
            const c = caixaLivre(p.caixa || padrao[k]);
            return { p, k, caixa: c, medidas: medidasCard(G, c.w / 100 * G.A4_L, c.h / 100 * G.A4_A) };
          }) };
      }
      const blocos = layoutPagina(G, pg.blocos, n === 0 ? 'capa' : 'interna');
      return { pg, n, tipo: n === 0 ? 'capa' : 'interna', blocos, sobra: blocos.flatMap(b => b.sobra) };
    });
  }, [grade, paginas]);

  // Onde cada produto está e com que medidas — pro editor e pros tamanhos de
  // card. Os tamanhos valem em qualquer estilo de bloco; a única exigência é o
  // bloco ter linhas/colunas pra eles. `semTamanho` diz, por tamanho, por que
  // não dá, pra tela explicar em vez de sumir com a opção.
  const ondeEsta = useMemo(() => {
    const m = new Map();
    const capa = 'Na capa da arte os quadros têm tamanho fixo. Pra mudar o tamanho, use a página 1 em montagem livre.';
    const umaLinha = 'O bloco tem 1 linha só — esse tamanho precisa de 2. Aumente as linhas do bloco.';
    layout.forEach(l => {
      if (l.tipo === 'arte') {
        l.cards.forEach(c => m.set(c.p.codpro, { medidas: c.medidas, semTamanho: { largo: capa, alto: capa, grande: capa } }));
      } else if (l.tipo === 'livre') {
        const livre = 'Na página livre o tamanho é o da caixa: puxe os cantos do card na página.';
        l.cards.forEach(c => m.set(c.p.codpro, { medidas: c.medidas, livre: { pagina: l.pg.id, caixa: c.caixa, n: l.n },
          semTamanho: { largo: livre, alto: livre, grande: livre } }));
      } else {
        l.blocos.forEach(b => b.cards.forEach(c => m.set(c.p.codpro, {
          medidas: c.medidas,
          semTamanho: { alto: b.linhas < 2 ? umaLinha : null, grande: b.linhas < 2 ? umaLinha : null },
        })));
      }
    });
    return m;
  }, [layout]);

  // A lista mostra todos os produtos; os que já estão no catálogo vêm pintados
  // (e o + abre o ajuste deles). "Adicionar todos" põe só os que faltam.
  const usados = useMemo(() => new Set(itens.map(i => i.codpro)), [itens]);
  const lista = useMemo(() => {
    const t = busca.trim().toLowerCase();
    return disponiveis.filter(p => !t || p.nome.toLowerCase().includes(t) || p.codpro.includes(t));
  }, [disponiveis, busca]);
  const faltam = useMemo(() => lista.filter(p => !usados.has(p.codpro)), [lista, usados]);

  // ---- Ações sobre produtos
  const adicionar = (p) => { setPaginas(prev => colocarNoProximo(prev, novoItem(p), grade.geometria)); setEditando(p.codpro); };
  const adicionarTodos = () => setPaginas(prev => faltam.reduce((acc, p) => colocarNoProximo(acc, novoItem(p), grade.geometria), prev));
  const remover = (codpro) => {
    setPaginas(prev => {
      const [semEle, item] = retirar(prev, codpro);
      if (item?.foto?.bruta) soltarUrl(item.foto.bruta);
      return semEle;
    });
    setEditando(null);
  };
  // `campos` pode ser uma função do item atual (arrasto no editor: não pisa numa
  // foto que terminou de ser tratada no meio do arrasto).
  const mudarItem = (codpro, campos) => setPaginas(prev => mudarItemEm(prev, codpro,
    it => ({ ...it, ...(typeof campos === 'function' ? campos(it) : campos) })));

  const limparTudo = async () => {
    const r = await Swal.fire({ icon: 'warning', title: `Tirar os ${itens.length} produtos do catálogo?`,
      text: 'As páginas e os blocos continuam, só vazios.',
      showCancelButton: true, confirmButtonText: 'Tirar todos', cancelButtonText: 'Cancelar' });
    if (r.isConfirmed) { setPaginas(prev => mapItens(prev, () => [])); setEditando(null); }
  };

  // Soltou algo num lugar: `destino` = { pagina, bloco? }, `pos` = antes de qual
  // posição (null = no fim).
  const soltar = (destino, pos) => {
    const a = arrastando.current;
    arrastando.current = null;
    setArrastandoTipo(null);
    if (!a) return;
    // Página livre: o card cai com o centro onde foi solto, no tamanho de um card da grade 4×5.
    const noPonto = (item) => {
      if (!destino.ponto) return item;
      const n = paginas.findIndex(pg => pg.id === destino.pagina);
      const base = vagaLivre(grade.geometria, n === 0 ? 'capa' : 'interna', []);
      return { ...item, caixa: caixaLivre({ ...base, x: destino.ponto.x - base.w / 2, y: destino.ponto.y - base.h / 2 }) };
    };
    if (a.tipo === 'novo') {
      setPaginas(prev => inserir(prev, destino, pos, noPonto(novoItem(a.produto))));
      setEditando(a.produto.codpro);
      return;
    }
    setPaginas(prev => {
      const [semEle, item] = retirar(prev, a.codpro);
      if (!item) return prev;
      // Entra no lugar do card onde foi solto, e ele anda uma casa. (Com o ajuste
      // de -1 que havia aqui, arrastar pra frente parava uma casa antes do alvo.)
      return inserir(semEle, destino, pos, noPonto(item));
    });
  };

  const comecarArrasto = (dados) => { arrastando.current = dados; setArrastandoTipo(dados.tipo); };
  const terminarArrasto = () => { arrastando.current = null; setArrastandoTipo(null); setSobreLixeira(false); setSobreLista(false); };

  // ---- Ações sobre páginas e blocos
  const mudarPagina = (pid, fn) => setPaginas(prev => prev.map(pg => (pg.id === pid ? fn(pg) : pg)));
  const mudarBloco = (pid, bid, campos) => mudarPagina(pid, pg => ({
    ...pg, blocos: pg.blocos.map(b => (b.id === bid ? { ...b, ...campos } : b)) }));

  const adicionarPagina = (tipo = 'grade') => setPaginas(prev => [...prev,
    tipo === 'livre' ? paginaLivre() : novaPagina([...prev].reverse().find(pg => !pg.arte && !pg.livre))]);

  // Troca o jeito da página (grade de blocos, livre ou, na 1ª, os quadros da arte).
  // Os produtos vêm junto: da grade pra livre cada um fica onde estava; da livre
  // pra grade, na ordem de leitura (de cima pra baixo, da esquerda pra direita).
  const mudarTipoPagina = (pid, tipo) => setPaginas(prev => prev.map((pg, n) => {
    if (pg.id !== pid || tipoPagina(pg) === tipo) return pg;
    const l = layout.find(x => x.pg.id === pid);
    let itens = todosItens([pg]);
    if (pg.livre) {
      const caixa = new Map(l.cards.map(c => [c.p.codpro, c.caixa]));
      itens = [...itens].sort((a, b) => (caixa.get(a.codpro).y - caixa.get(b.codpro).y) || (caixa.get(a.codpro).x - caixa.get(b.codpro).x));
    }
    if (tipo === 'arte') return capaArte(itens);
    if (tipo === 'livre') {
      const G = grade.geometria;
      const ondeMM = new Map((l.cards || l.blocos?.flatMap(b => b.cards) || []).map(c => [c.p.codpro,
        c.caixa || (c.x != null ? { x: c.x / G.A4_L * 100, y: c.y / G.A4_A * 100, w: c.w / G.A4_L * 100, h: c.h / G.A4_A * 100 }
          : grade.capa[c.k] && { x: grade.capa[c.k].x * 100, y: grade.capa[c.k].y * 100, w: grade.capa[c.k].w * 100, h: grade.capa[c.k].h * 100 })]));
      const estilo = pg.blocos?.[0]?.estilo === 'soltos' ? 'soltos' : 'quadros';
      return { ...paginaLivre([], estilo), id: pg.id, itens: itens.map(i => ({ ...i, caixa: ondeMM.get(i.codpro) || null })) };
    }
    const nova = { ...novaPagina({ blocos: [{ estilo: pg.estilo === 'soltos' ? 'soltos' : 'quadros', cols: 4, linhas: n === 0 ? 4 : 5 }] }), id: pg.id };
    nova.blocos[0].itens = itens;
    return nova;
  }));

  // Página livre: mexer na caixa de um produto, arrumar tudo em grade, frente/trás.
  const mudarCaixa = useCallback((codpro, caixa) => setPaginas(prev => mudarItemEm(prev, codpro, it => ({ ...it, caixa: caixaLivre(caixa) }))), []);
  const organizarLivre = (pid) => setPaginas(prev => prev.map((pg, n) => {
    if (pg.id !== pid) return pg;
    const caixas = gradeLivre(grade.geometria, n === 0 ? 'capa' : 'interna', pg.itens.length);
    return { ...pg, itens: pg.itens.map((it, k) => ({ ...it, caixa: caixas[k] })) };
  }));
  // Teclado: setas movem o card selecionado da página livre (Shift anda mais), Esc
  // fecha o painel. Só com a tela à vista (no Estúdio as outras abas ficam escondidas).
  const raiz = useRef(null);
  useEffect(() => {
    const tecla = (e) => {
      if (!raiz.current?.offsetParent || /INPUT|TEXTAREA|SELECT/.test(e.target.tagName)) return;
      if (e.key === 'Escape' && editando) { setEditando(null); return; }
      const d = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] }[e.key];
      const lv = editando && ondeEsta.get(editando)?.livre;
      if (!d || !lv) return;
      e.preventDefault();
      const passo = e.shiftKey ? 2 : 0.3;
      mudarCaixa(editando, { ...lv.caixa, x: lv.caixa.x + d[0] * passo, y: lv.caixa.y + d[1] * passo });
    };
    window.addEventListener('keydown', tecla);
    return () => window.removeEventListener('keydown', tecla);
  }, [editando, ondeEsta, mudarCaixa]);

  const ordemLivre = (codpro, praFrente) => setPaginas(prev => prev.map(pg => {
    if (!pg.livre || !pg.itens.some(i => i.codpro === codpro)) return pg;
    const it = pg.itens.find(i => i.codpro === codpro), resto = pg.itens.filter(i => i.codpro !== codpro);
    return { ...pg, itens: praFrente ? [...resto, it] : [it, ...resto] };
  }));

  const removerPagina = async (pid) => {
    const pg = paginas.find(p => p.id === pid);
    const qtd = todosItens([pg]).length;
    if (qtd) {
      const r = await Swal.fire({ icon: 'warning', title: 'Remover a página?',
        text: `Os ${qtd} produtos dela voltam pra lista.`,
        showCancelButton: true, confirmButtonText: 'Remover', cancelButtonText: 'Cancelar' });
      if (!r.isConfirmed) return;
    }
    setPaginas(prev => {
      const resto = prev.filter(p => p.id !== pid);
      return resto.length ? resto : [capaArte()];
    });
  };

  const moverPagina = (pid, delta) => setPaginas(prev => {
    const i = prev.findIndex(p => p.id === pid), j = i + delta;
    // A capa da arte fica sempre em primeiro.
    if (j < 0 || j >= prev.length || prev[i].arte || prev[j].arte) return prev;
    const nova = [...prev];
    [nova[i], nova[j]] = [nova[j], nova[i]];
    return nova;
  });

  const adicionarBloco = (pid) => mudarPagina(pid, pg => {
    const ult = pg.blocos[pg.blocos.length - 1];
    return { ...pg, blocos: [...pg.blocos, novoBloco({ estilo: ult?.estilo, cols: ult?.cols, linhas: 1 })] };
  });

  const removerBloco = async (pid, bid) => {
    const pg = paginas.find(p => p.id === pid);
    const b = pg.blocos.find(x => x.id === bid);
    if (b.itens.length) {
      const r = await Swal.fire({ icon: 'warning', title: 'Remover o bloco?',
        text: `Os ${b.itens.length} produtos dele voltam pra lista.`,
        showCancelButton: true, confirmButtonText: 'Remover', cancelButtonText: 'Cancelar' });
      if (!r.isConfirmed) return;
    }
    mudarPagina(pid, p => ({ ...p, blocos: p.blocos.filter(x => x.id !== bid) }));
  };

  const moverBloco = (pid, bid, delta) => mudarPagina(pid, pg => {
    const i = pg.blocos.findIndex(b => b.id === bid), j = i + delta;
    if (j < 0 || j >= pg.blocos.length) return pg;
    const blocos = [...pg.blocos];
    [blocos[i], blocos[j]] = [blocos[j], blocos[i]];
    return { ...pg, blocos };
  });

  // ---- Salvar
  // Página livre vai com a caixa que está na tela (a de quem ainda não foi arrastado é a da grade padrão).
  const caixaNaTela = new Map(layout.filter(l => l.tipo === 'livre').flatMap(l => l.cards.map(c => [c.p.codpro, c.caixa])));
  const estruturaParaEnviar = (soCodigo) => paginas.map(pg => (pg.arte
    ? { arte: true, itens: pg.itens.map(soCodigo ? (i => i.codpro) : itemParaSalvar) }
    : pg.livre
      ? { livre: true, estilo: pg.estilo, itens: pg.itens.map(i => (soCodigo
        ? { codpro: i.codpro, ...caixaNaTela.get(i.codpro) }
        : itemParaSalvar({ ...i, caixa: caixaNaTela.get(i.codpro) || i.caixa }))) }
      : { blocos: pg.blocos.map(b => ({ estilo: b.estilo, cols: b.cols, linhas: b.linhas,
        itens: b.itens.map(soCodigo ? (i => i.codpro) : itemParaSalvar) })) }));

  // Mudança não salva = o que vai no Salvar, menos o estilo que o recorte
  // sugeriu sozinho (só conta o escolhido à mão).
  const assinatura = useMemo(() => JSON.stringify({ validade, p: paginas.map(pg => (pg.arte || pg.livre
    ? [tipoPagina(pg), pg.estilo || '', pg.itens.map(itemAssinado)]
    : pg.blocos.map(b => [b.estilo, b.cols, b.linhas, b.itens.map(itemAssinado)]))) }), [paginas, validade]);
  const [salvo, setSalvo] = useState(null);
  useEffect(() => { if (!carregando && salvo === null) setSalvo(assinatura); }, [carregando, salvo, assinatura]);
  const sujo = salvo !== null && assinatura !== salvo;
  useEffect(() => { onResumo?.({ qtd: itens.length, sujo }); }, [onResumo, itens.length, sujo]);

  const salvar = async () => {
    setSalvando(true);
    const assinando = assinatura;
    try {
      await axios.put(`${API}/catalogo/campanha/${id}/layout`, { versao: 2, validade, paginas: estruturaParaEnviar(false) });
      setSalvo(assinando);
      Swal.fire({ icon: 'success', title: 'Montagem salva', timer: 1200, showConfirmButton: false });
    } catch (e) {
      Swal.fire('Erro', e.response?.data?.error || 'Não consegui salvar.', 'error');
    } finally { setSalvando(false); }
  };

  // Fotos tratadas sobem uma por vez antes de gerar (todas juntas estouravam o
  // limite do corpo do pedido).
  const sincronizarFotos = async () => {
    const pendentes = itens.filter(it => it.foto.url && !it.foto.sincronizada);
    for (let k = 0; k < pendentes.length; k += 5) {
      await Promise.all(pendentes.slice(k, k + 5).map(it =>
        axios.post(`${API}/catalogo/campanha/${id}/pronta/${it.codpro}`, { dados: it.foto.url })
          .catch(err => {
            // Diz QUAL produto travou — sem isso a gerente não tem por onde começar.
            const motivo = err.response?.data?.error || err.message;
            throw Object.assign(new Error(`Foto do Cód.${it.codpro}: ${motivo}`), { semResposta: false });
          })));
    }
    if (pendentes.length) {
      // Marca como enviada só a foto que de fato subiu: se ela mudou enquanto o
      // envio rodava, a nova continua pendente e sobe na próxima geração.
      const enviadas = new Map(pendentes.map(it => [it.codpro, it.foto.url]));
      setPaginas(prev => mapItens(prev, lst => lst.map(it => (enviadas.get(it.codpro) === it.foto.url
        ? { ...it, foto: { ...it.foto, sincronizada: true } } : it))));
    }
  };

  const gerar = async ({ previa: soVer = false } = {}) => {
    if (!itens.length) return Swal.fire('Atenção', 'Coloque pelo menos um produto.', 'info');
    // Foto ainda sendo tratada sairia com a versão antiga (ou sem foto). Espera.
    const tratando = itens.filter(i => i.foto.carregando && !i.foto.erro).length;
    if (tratando) {
      return Swal.fire({ icon: 'info', title: 'Fotos ainda sendo tratadas',
        text: `Faltam ${tratando} foto(s). Espere terminar (os cards param de girar) e gere de novo.` });
    }
    // Avisos antes de gerar: nada pode sumir do catálogo sem ela saber.
    const sobraram = layout.flatMap(l => l.sobra);
    const vazias = layout.filter(l => (l.blocos ? l.blocos.reduce((s, b) => s + b.cards.length, 0) : l.cards.length) === 0);
    const semFoto = itens.filter(i => !i.foto.url);
    const avisos = [];
    if (sobraram.length) avisos.push(`<b>${sobraram.length} produto(s) não couberam</b> e ficam de fora: ${sobraram.map(i => `Cód.${i.codpro}`).join(', ')}`);
    if (vazias.length) avisos.push(`<b>${vazias.length} página(s) vazia(s)</b> vão sair em branco: ${vazias.map(l => l.n + 1).join(', ')}`);
    if (semFoto.length && !soVer) avisos.push(`<b>${semFoto.length} produto(s) sem foto</b>: ${semFoto.map(i => `Cód.${i.codpro}`).join(', ')}`);
    if (avisos.length && !soVer) {
      const r = await Swal.fire({ icon: 'warning', title: 'Antes de gerar',
        html: `<div style="text-align:left;font-size:0.9rem">${avisos.join('<br><br>')}</div>`,
        showCancelButton: true, confirmButtonText: 'Gerar assim', cancelButtonText: 'Voltar' });
      if (!r.isConfirmed) return;
    }
    setGerando(true);
    Swal.fire({ title: 'Gerando o catálogo...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
    try {
      await sincronizarFotos();
      const r = await axios.post(`${API}/catalogo/campanha/${id}/pdf`, {
        validade,
        paginas: estruturaParaEnviar(true),
        // Preço e unidade o servidor pega do banco. `comFoto` diz se o item tem
        // foto agora — sem foto, o servidor não usa a que estiver no cache.
        itens: itens.map(i => ({ codpro: i.codpro, nome: i.nome, grande: tamanhoDe(i) === 'grande', tamanho: tamanhoDe(i),
          comFoto: !!i.foto.url, preencher: i.foto.cobre, zoom: i.foto.zoom, x: i.foto.x, y: i.foto.y,
          folha: i.folha, tarja: i.tarja, mostrarDe: i.mostrarDe !== false })),
      }, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      Swal.close();
      if (soVer) {
        setPrevia(ant => { if (ant) URL.revokeObjectURL(ant); return url; });
      } else {
        const a = document.createElement('a');
        a.href = url;
        a.download = `Catalogo - ${(campanha?.nome || 'campanha').replace(/[\\/:*?"<>|]/g, '-')}.pdf`;
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
      }
    } catch (e) {
      // O motivo pode vir como arquivo (pedido do PDF) ou como JSON (envio das
      // fotos) — antes só o primeiro era lido, e o resto virava mensagem genérica.
      let msg = 'Não consegui gerar o catálogo.';
      const dados = e.response?.data;
      try {
        const corpo = dados instanceof Blob ? JSON.parse(await dados.text()) : dados;
        if (corpo?.error) msg = corpo.error;
      } catch { /* resposta sem JSON */ }
      if (!e.response) msg = e.message?.startsWith('Foto do Cód.') ? e.message : `Sem resposta do servidor (${e.message}).`;
      Swal.fire('Erro', msg, 'error');
    } finally { setGerando(false); }
  };

  if (carregando || !grade) {
    return <div className="p-4"><span className="spinner-border spinner-border-sm me-2"></span>Carregando...</div>;
  }

  const itemEditando = editando ? itens.find(i => i.codpro === editando) : null;
  const local = editando ? ondeEsta.get(editando) : null;
  const GAP = 18;
  const larguraPagina = Math.max(150, zoom === 'largura' ? tamCol.l - 14
    : zoom === 'inteira' ? Math.min(tamCol.l - 14, (tamCol.a - 70) * grade.geometria.A4_L / grade.geometria.A4_A)
      : (tamCol.l - 14 - GAP * (zoom - 1)) / zoom);
  const compacto = larguraPagina < 400;
  const contexto = { grade, visual, arrastando, arrastandoTipo, comecarArrasto, terminarArrasto, soltar, abrir: setEditando,
    sel: editando, mudarCaixa, mudarItem, compacto };
  const passoZoom = (d) => setZoom(z => ZOOMS[limitar(ZOOMS.findIndex(o => o.v === z) + d, 0, ZOOMS.length - 1)].v);

  return (
    // Dois painéis com rolagem própria: a lista fica sempre à vista enquanto as
    // páginas rolam. (O "grudar ao rolar" não funciona nesta aplicação por causa
    // do overflow-x global do html/body.)
    <div ref={raiz} style={embutido ? { display: 'flex', flexDirection: 'column', flex: '1 1 0', minHeight: 0 }
      : { display: 'flex', flexDirection: 'column', height: 'calc(100vh - 56px)', minHeight: 520 }}>
      <div className={embutido ? 'barra-c' : 'd-flex justify-content-between align-items-center flex-wrap gap-2 mb-2'}>
        <div>
          {!embutido && <>
            <button className="btn btn-sm btn-outline-secondary me-2" onClick={() => navigate(`/campanhas/${id}/produtos`)}>
              <i className="bi bi-arrow-left"></i>
            </button>
            <span className="fw-bold" style={{ fontSize: '1.1rem' }}>Catálogo — {campanha?.nome}</span>
          </>}
          <span className={embutido ? 'info' : 'text-muted ms-2'} style={{ fontSize: '0.85rem' }}>
            {itens.length} produto(s) · {paginas.length} página(s){sujo && <span className="ms-2" style={{ color: '#A8460A' }}>• não salvo</span>}
          </span>
          <AvisoRecorteIA />
        </div>
        <div className="d-flex align-items-center gap-2 flex-wrap ms-auto">
          {/* Zoom: − afasta (mais páginas por linha), + aproxima. */}
          <div className="d-flex align-items-center gap-1" title="Zoom das páginas">
            <button className="btn btn-sm btn-outline-secondary" style={{ padding: '1px 8px' }} onClick={() => passoZoom(1)}
              disabled={zoom === ZOOMS[ZOOMS.length - 1].v} title="Afastar (ver mais páginas)"><i className="bi bi-zoom-out"></i></button>
            <select className="form-select form-select-sm" style={{ width: 'auto' }} value={String(zoom)}
              onChange={e => setZoom(ZOOMS.find(o => String(o.v) === e.target.value).v)}>
              {ZOOMS.map(o => <option key={o.v} value={String(o.v)}>{o.rotulo}</option>)}
            </select>
            <button className="btn btn-sm btn-outline-secondary" style={{ padding: '1px 8px' }} onClick={() => passoZoom(-1)}
              disabled={zoom === ZOOMS[0].v} title="Aproximar"><i className="bi bi-zoom-in"></i></button>
          </div>
          <label className="text-muted" style={{ fontSize: '0.82rem' }}>Válido até</label>
          <input className="form-control form-control-sm" style={{ width: 90 }} value={validade}
            onChange={e => setValidade(e.target.value)} placeholder="30/11" />
          <button className="btn btn-sm btn-outline-secondary" onClick={salvar} disabled={salvando}>
            <i className="bi bi-save me-1"></i>{salvando ? 'Salvando...' : 'Salvar montagem'}
          </button>
          <button className="btn btn-sm btn-outline-success" onClick={() => gerar({ previa: true })}
            disabled={gerando || !itens.length}>
            <i className="bi bi-eye me-1"></i>Ver prévia
          </button>
          <button className={`btn btn-sm ${embutido ? 'btn-acao-e' : 'btn-success'}`} onClick={() => gerar()} disabled={gerando || !itens.length}>
            <i className="bi bi-download me-1"></i>Baixar PDF
          </button>
        </div>
      </div>

      <div style={{ flex: 1, minHeight: 0, display: 'flex', gap: 12 }}>
        {/* ---------------- Produtos disponíveis ---------------- */}
        <div className="card p-2"
          onDragOver={e => { if (arrastando.current?.tipo === 'item') { e.preventDefault(); setSobreLista(true); } }}
          onDragLeave={() => setSobreLista(false)}
          onDrop={e => {
            e.preventDefault(); setSobreLista(false);
            const a = arrastando.current;
            if (a?.tipo === 'item') remover(a.codpro);
            terminarArrasto();
          }}
          style={{ width: 300, flexShrink: 0, display: 'flex', flexDirection: 'column', minHeight: 0,
            outline: sobreLista ? '2px dashed #b23b3b' : 'none', outlineOffset: 2 }}>
          {sobreLista && (
            <div className="text-center mb-2 py-2" style={{ background: '#fdeeee', color: '#b23b3b',
              borderRadius: 6, fontSize: '0.82rem', fontWeight: 600 }}>
              <i className="bi bi-trash me-1"></i>Solte pra tirar do catálogo
            </div>
          )}
          <input className="form-control form-control-sm mb-2" placeholder="Buscar produto..."
            value={busca} onChange={e => setBusca(e.target.value)} />
          <div className="d-flex gap-1 mb-1">
            <button className="btn btn-sm btn-outline-success flex-fill" style={{ fontSize: '0.74rem' }}
              disabled={!faltam.length} onClick={adicionarTodos}>
              <i className="bi bi-plus-lg me-1"></i>Adicionar {busca ? 'os achados' : 'todos'} ({faltam.length})
            </button>
            <button className="btn btn-sm btn-outline-danger" style={{ fontSize: '0.74rem' }}
              disabled={!itens.length} onClick={limparTudo} title="Tirar todos do catálogo">
              <i className="bi bi-trash"></i>
            </button>
          </div>
          <small className="text-muted mb-1" style={{ fontSize: '0.7rem' }}>
            Arraste pro lugar exato, ou clique no + pra ir na próxima vaga.
          </small>
          <LegendaUso peca="catalogo" />
          <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
            {lista.length === 0 && <div className="text-muted p-2" style={{ fontSize: '0.84rem' }}>
              {disponiveis.length ? 'Nenhum produto com esse nome.' : 'Nenhum produto na campanha.'}
            </div>}
            {lista.map(p => {
              const usado = usados.has(p.codpro);
              return (
                <div key={p.codpro} className="d-flex align-items-center gap-2 p-1 border-bottom"
                  draggable={!usado}
                  onDragStart={e => { e.dataTransfer.setData('text/plain', p.codpro); comecarArrasto({ tipo: 'novo', produto: p }); }}
                  onDragEnd={terminarArrasto}
                  style={{ cursor: usado ? 'default' : 'grab', ...estiloLinhaUsada(usado, editando === p.codpro) }}
                  title={usado ? 'Já está no catálogo — clique no lápis pra ajustar' : 'Arraste pra uma página, ou clique no + '}>
                  <i className={`bi ${usado ? 'bi-check2' : 'bi-grip-vertical'} text-muted`} style={{ fontSize: '0.8rem' }}></i>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="fw-semibold text-truncate" style={{ fontSize: '0.8rem' }}>{p.nome}</div>
                    <div className="text-muted" style={{ fontSize: '0.72rem' }}>
                      Cód.{p.codpro} · R$ {fmt(p.por)}
                      {!p.temFoto && <span className="badge bg-warning text-dark ms-1" style={{ fontSize: '0.6rem' }}>sem foto</span>}
                      <MarcasOutrasPecas uso={uso} codpro={p.codpro} peca="catalogo" />
                    </div>
                  </div>
                  {usado
                    ? <button className="btn btn-sm btn-link text-secondary p-0" onClick={() => setEditando(p.codpro)} title="Ajustar no catálogo">
                        <i className="bi bi-pencil"></i></button>
                    : <button className="btn btn-sm btn-link text-success p-0" onClick={() => adicionar(p)} title="Pôr na próxima vaga">
                        <i className="bi bi-plus-lg"></i></button>}
                </div>
              );
            })}
          </div>
        </div>

        {/* ---------------- Páginas (com zoom: uma ou várias por linha) ---------------- */}
        <div ref={colPaginas} style={{ flex: 1, minWidth: 0, overflowY: 'auto', paddingRight: 4 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: GAP, alignItems: 'flex-start', justifyContent: zoom === 'inteira' ? 'center' : 'flex-start' }}>
            {layout.map((l, n) => (
              <div key={l.pg.id} style={{ width: larguraPagina }}>
                <PaginaEditavel l={l} total={paginas.length} ctx={contexto}
                  acimaEhArte={n > 0 && !!paginas[n - 1].arte}
                  onTipo={(tipo) => mudarTipoPagina(l.pg.id, tipo)}
                  onEstiloLivre={(estilo) => mudarPagina(l.pg.id, pg => ({ ...pg, estilo }))}
                  onOrganizar={() => organizarLivre(l.pg.id)}
                  onMoverPagina={(d) => moverPagina(l.pg.id, d)}
                  onRemoverPagina={() => removerPagina(l.pg.id)}
                  onAdicionarBloco={() => adicionarBloco(l.pg.id)}
                  onMudarBloco={(bid, campos) => mudarBloco(l.pg.id, bid, campos)}
                  onMoverBloco={(bid, d) => moverBloco(l.pg.id, bid, d)}
                  onRemoverBloco={(bid) => removerBloco(l.pg.id, bid)} />
              </div>
            ))}
            <div style={{ width: larguraPagina, display: 'flex', flexDirection: 'column', gap: 8, paddingTop: compacto ? 0 : 26 }}>
              <button className="btn btn-outline-secondary w-100" onClick={() => adicionarPagina('grade')} style={{ borderStyle: 'dashed', padding: '12px 0' }}>
                <i className="bi bi-grid-3x3-gap me-2"></i>Nova página em grade
              </button>
              <button className="btn btn-outline-secondary w-100" onClick={() => adicionarPagina('livre')} style={{ borderStyle: 'dashed', padding: '12px 0' }}
                title="Cada produto onde você quiser, no tamanho que quiser — igual ao story">
                <i className="bi bi-arrows-move me-2"></i>Nova página livre
              </button>
            </div>
          </div>
        </div>

        {/* ---------------- Ajustes do produto (painel do lado, como no story) ---------------- */}
        <div className="card p-2" style={{ width: 310, flexShrink: 0, overflowY: 'auto' }}>
          {itemEditando
            ? <AjustesItem key={itemEditando.codpro}
                item={itemEditando} semTamanho={local?.semTamanho || {}} medidas={local?.medidas} livre={local?.livre || null}
                campanhaId={id}
                onFechar={() => setEditando(null)}
                onMudar={(campos) => mudarItem(itemEditando.codpro, campos)}
                onFoto={(mudanca) => mudarFoto(itemEditando.codpro, mudanca)}
                onCaixa={(c) => mudarCaixa(itemEditando.codpro, c)}
                onOrdem={(frente) => ordemLivre(itemEditando.codpro, frente)}
                onRemover={() => remover(itemEditando.codpro)} />
            : <div className="text-muted" style={{ fontSize: '0.8rem' }}>
                <div className="fw-bold mb-1" style={{ color: '#2a1f26', fontSize: '0.88rem' }}>Ajustes do produto</div>
                Clique num produto da página pra ajustar aqui: texto, tamanho, foto, preço e "De:".
                <hr className="my-2" />
                <div className="mb-1"><b>Página livre</b>: cada produto onde você quiser — arraste o card e puxe os cantos, como no story. As linhas laranja mostram quando ele encosta no meio ou na borda de outro.</div>
                <div className="mb-1"><b>Grade</b>: blocos com faixa ou moldura, produtos em ordem — arraste um card pra trocar de lugar.</div>
                <div><b>Zoom</b>: o − lá em cima mostra várias páginas por linha, pra ver o catálogo todo.</div>
              </div>}
        </div>
      </div>

      {/* Lixeira flutuante: aparece arrastando um produto do catálogo. */}
      {arrastandoTipo === 'item' && (
        <div
          onDragOver={e => { e.preventDefault(); setSobreLixeira(true); }}
          onDragLeave={() => setSobreLixeira(false)}
          onDrop={e => {
            e.preventDefault();
            const a = arrastando.current;
            if (a?.tipo === 'item') remover(a.codpro);
            terminarArrasto();
          }}
          style={{
            position: 'fixed', left: '50%', bottom: 24, transform: 'translateX(-50%)', zIndex: 9998,
            background: sobreLixeira ? '#b23b3b' : 'rgba(178,59,59,0.88)', color: '#fff',
            padding: '12px 26px', borderRadius: 30, fontWeight: 600, fontSize: '0.92rem',
            boxShadow: '0 6px 20px rgba(0,0,0,0.3)', border: sobreLixeira ? '2px dashed #fff' : '2px solid transparent',
          }}>
          <i className="bi bi-trash me-2"></i>Solte aqui pra tirar do catálogo
        </div>
      )}

      {/* Prévia do PDF — abre aqui mesmo, sem baixar. */}
      {previa && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 10000,
          display: 'flex', flexDirection: 'column', padding: 16 }}
          onClick={() => { URL.revokeObjectURL(previa); setPrevia(null); }}>
          <div className="d-flex justify-content-between align-items-center mb-2" style={{ color: '#fff' }}
            onClick={e => e.stopPropagation()}>
            <span className="fw-bold"><i className="bi bi-file-earmark-pdf me-2"></i>Prévia do catálogo</span>
            <div className="d-flex gap-2">
              <button className="btn btn-sm btn-success" onClick={() => gerar()}>
                <i className="bi bi-download me-1"></i>Baixar
              </button>
              <button className="btn btn-sm btn-light" onClick={() => { URL.revokeObjectURL(previa); setPrevia(null); }}>
                <i className="bi bi-x-lg"></i>
              </button>
            </div>
          </div>
          <iframe title="Prévia do catálogo" src={previa} onClick={e => e.stopPropagation()}
            style={{ flex: 1, width: '100%', border: 'none', borderRadius: 8, background: '#fff' }} />
        </div>
      )}

    </div>
  );
}

// ---------------------------------------------------------------------------
// Uma página: barra com as opções dela, barra de cada bloco e o desenho.
// ---------------------------------------------------------------------------
const pct = (v, total) => `${v / total * 100}%`;

function PaginaEditavel({ l, total, ctx, acimaEhArte, onTipo, onEstiloLivre, onOrganizar, onMoverPagina, onRemoverPagina,
  onAdicionarBloco, onMudarBloco, onMoverBloco, onRemoverBloco }) {
  const { grade, compacto } = ctx;
  const G = grade.geometria;
  const n = l.n;
  const arte = l.tipo === 'arte';
  const livre = l.tipo === 'livre';
  const btn = { fontSize: '0.75rem', padding: '1px 7px' };
  // Página cheia: mais uma linha deixaria a folha do preço pequena demais.
  const tipoPag = n === 0 ? 'capa' : 'interna';
  const cabeLinha = (extra) => !!l.blocos && alturaLinhaPagina(G, [...l.blocos, ...extra], tipoPag) >= G.ALTURA_MIN;
  const podeMaisLinha = (bid) => !!l.blocos && alturaLinhaPagina(G,
    l.blocos.map(b => (b.id === bid ? { ...b, linhas: b.linhas + 1 } : b)), tipoPag) >= G.ALTURA_MIN;
  const avisoCheia = 'Página cheia: mais uma linha deixaria o preço pequeno demais. Use outra página.';
  const sel = { width: 'auto', fontSize: '0.76rem' };

  return (
    <div className="mb-3">
      {/* ---- barra da página ---- */}
      <div className="d-flex align-items-center gap-1 flex-wrap mb-1" style={{ fontSize: '0.82rem' }}>
        <b className="me-1">{compacto ? `Pág. ${n + 1}` : `Página ${n + 1}`}</b>
        {/* Jeito da página: grade de blocos, livre (cada produto onde quiser) ou, na 1ª, os quadros da arte. */}
        <select className="form-select form-select-sm" style={sel} value={l.tipo === 'arte' ? 'arte' : livre ? 'livre' : 'grade'}
          title="Jeito da página" onChange={e => onTipo(e.target.value)}>
          {n === 0 && <option value="arte">Quadros da arte</option>}
          <option value="grade">Grade (blocos)</option>
          <option value="livre">Livre (arrastar)</option>
        </select>
        {livre && !compacto && <>
          <select className="form-select form-select-sm" style={sel} value={l.pg.estilo || 'quadros'} onChange={e => onEstiloLivre(e.target.value)}>
            <option value="quadros">Com moldura</option>
            <option value="soltos">Soltos</option>
          </select>
          <button className="btn btn-sm btn-outline-secondary" style={btn} onClick={onOrganizar} disabled={!l.cards.length}
            title="Arruma os produtos desta página em grade (depois dá pra arrastar de novo)">
            <i className="bi bi-grid-3x3-gap me-1"></i>Organizar
          </button>
        </>}
        {!arte && !livre && (
          <button className="btn btn-sm btn-outline-secondary" style={btn} onClick={onAdicionarBloco}
            disabled={!cabeLinha([{ linhas: 1 }])} title={cabeLinha([{ linhas: 1 }]) ? 'Novo bloco no fim da página' : avisoCheia}>
            <i className="bi bi-plus-lg"></i>{!compacto && ' Bloco'}
          </button>
        )}
        <span className="ms-auto d-flex gap-1">
          {!arte && <>
            <button className="btn btn-sm btn-outline-secondary" style={btn} title="Subir a página"
              disabled={n === 0 || acimaEhArte} onClick={() => onMoverPagina(-1)}>
              <i className="bi bi-arrow-up"></i>
            </button>
            <button className="btn btn-sm btn-outline-secondary" style={btn} title="Descer a página"
              disabled={n === total - 1} onClick={() => onMoverPagina(1)}>
              <i className="bi bi-arrow-down"></i>
            </button>
          </>}
          <button className="btn btn-sm btn-outline-danger" style={btn} title="Remover a página" onClick={onRemoverPagina}>
            <i className="bi bi-trash"></i>
          </button>
        </span>
      </div>

      {/* ---- barra de cada bloco (com o zoom pequeno some: aumente o zoom pra mexer nos blocos) ---- */}
      {l.blocos && !compacto && l.blocos.map((b, i) => (
        <div key={b.id} className="d-flex align-items-center gap-1 flex-wrap mb-1 ps-2"
          style={{ fontSize: '0.76rem', borderLeft: `3px solid ${b.estilo === 'amarela' ? OURO : b.estilo === 'branca' ? '#bbb' : '#F7A233'}` }}>
          <span className="text-muted me-1">Bloco {i + 1}</span>
          <select className="form-select form-select-sm" style={{ width: 'auto', fontSize: '0.76rem' }}
            value={b.estilo} onChange={e => onMudarBloco(b.id, { estilo: e.target.value })}>
            {ESTILOS_BLOCO.map(o => <option key={o.valor} value={o.valor}>{o.rotulo}</option>)}
          </select>
          <select className="form-select form-select-sm" style={{ width: 'auto', fontSize: '0.76rem' }}
            value={b.cols} onChange={e => onMudarBloco(b.id, { cols: Number(e.target.value) })}>
            {G.COLUNAS.map(c => <option key={c} value={c}>{c} por linha</option>)}
          </select>
          <span className="d-inline-flex align-items-center gap-1 ms-1">
            <button className="btn btn-sm btn-outline-secondary" style={btn} disabled={b.linhas <= 1}
              onClick={() => onMudarBloco(b.id, { linhas: b.linhas - 1 })}>−</button>
            <span style={{ minWidth: 58, textAlign: 'center' }}>{b.linhas} linha{b.linhas > 1 ? 's' : ''}</span>
            <button className="btn btn-sm btn-outline-secondary" style={btn}
              disabled={b.linhas >= G.MAX_LINHAS || !podeMaisLinha(b.id)}
              title={podeMaisLinha(b.id) ? 'Mais uma linha' : avisoCheia}
              onClick={() => onMudarBloco(b.id, { linhas: b.linhas + 1 })}>+</button>
          </span>
          <span className="text-muted ms-1">{b.cards.length}/{b.cols * b.linhas}</span>
          <span className="ms-auto d-flex gap-1">
            <button className="btn btn-sm btn-outline-secondary" style={btn} disabled={i === 0} title="Subir o bloco"
              onClick={() => onMoverBloco(b.id, -1)}><i className="bi bi-arrow-up"></i></button>
            <button className="btn btn-sm btn-outline-secondary" style={btn} disabled={i === l.blocos.length - 1}
              title="Descer o bloco" onClick={() => onMoverBloco(b.id, 1)}><i className="bi bi-arrow-down"></i></button>
            <button className="btn btn-sm btn-outline-danger" style={btn} title="Remover o bloco"
              onClick={() => onRemoverBloco(b.id)}><i className="bi bi-x-lg"></i></button>
          </span>
        </div>
      ))}

      {/* ---- desenho da página ---- */}
      <FolhaA4 l={l} ctx={ctx} />

      {/* ---- o que não coube ---- */}
      {l.sobra.length > 0 && (
        <div className="mt-1 p-2" style={{ background: '#fdeeee', border: '1px solid #f1b8b8', borderRadius: 6, fontSize: '0.78rem' }}>
          <b style={{ color: '#b23b3b' }}><i className="bi bi-exclamation-triangle me-1"></i>
            {l.sobra.length} produto(s) não couberam nesta página</b>
          <span className="text-muted"> — aumente as linhas do bloco ou arraste pra outro lugar:</span>
          <div className="d-flex flex-wrap gap-1 mt-1">
            {l.sobra.map(p => (
              <span key={p.codpro} className="badge bg-light text-dark border" style={{ cursor: 'grab' }}
                draggable onDragStart={e => { e.dataTransfer.setData('text/plain', p.codpro); ctx.comecarArrasto({ tipo: 'item', codpro: p.codpro }); }}
                onDragEnd={ctx.terminarArrasto}>
                Cód.{p.codpro}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Desenho da página em escala (A4), com as zonas de soltar.
function FolhaA4({ l, ctx }) {
  const { grade, arrastandoTipo, soltar } = ctx;
  const G = grade.geometria;
  const A4L = G.A4_L, A4A = G.A4_A;
  const arrastandoAlgo = !!arrastandoTipo;
  const [sobreBloco, setSobreBloco] = useState(null);
  const n = l.n;
  const cab = l.tipo === 'arte' ? '22.9%' : (n === 0 ? '23.45%' : '12%');
  // A arte da página, a mesma do PDF: a de 2025, a página da campanha ou fundo + topo.
  const v = ctx.visual?.[l.tipo === 'arte' ? 'capa' : n === 0 ? 'livre' : 'miolo'];
  const caixaTopo = G.CABECALHO?.[n === 0 ? 'capa' : 'miolo'];
  const camada = { position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' };

  return (
    <div style={{
      position: 'relative', width: '100%', aspectRatio: `${A4L} / ${A4A}`,
      background: v ? '#0b0b0b' : 'linear-gradient(160deg,#1a0f05,#0b0b0b 60%)',
      border: '1px solid #333', borderRadius: 6, overflow: 'hidden', fontFamily: 'Poppins, sans-serif',
    }}>
      {v?.arte && <img src={v.arte} alt="" draggable={false} style={{ ...camada, objectFit: 'fill' }} />}
      {v?.fundo && <img src={v.fundo} alt="" draggable={false} style={{ ...camada, objectFit: 'cover' }} />}
      {v?.topo && caixaTopo && (
        <img src={v.topo} alt="" draggable={false} style={{ position: 'absolute', pointerEvents: 'none', objectFit: 'contain',
          left: `${caixaTopo.x}%`, top: `${caixaTopo.y}%`, width: `${caixaTopo.w}%`, height: `${caixaTopo.h}%` }} />
      )}
      {!v && (
        <div style={{
          position: 'absolute', left: 0, right: 0, top: 0, height: cab,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#F7A233', fontWeight: 800, letterSpacing: 1,
          fontSize: n === 0 ? '1.1rem' : '0.8rem', opacity: 0.5,
        }}>CABEÇALHO DA ARTE</div>
      )}

      {l.tipo === 'arte' ? (
        <>
          {grade.capa.map((s, k) => {
            const card = l.cards[k];
            const pos = { left: `${s.x * 100}%`, top: `${s.y * 100}%`, width: `${s.w * 100}%`, height: `${s.h * 100}%` };
            if (card) {
              return (
                <div key={card.p.codpro} style={{ position: 'absolute', ...pos, zIndex: 2 }}>
                  <CardPrevia item={card.p} medidas={card.medidas} solto={false} ctx={ctx}
                    destino={{ pagina: l.pg.id }} pos={k} />
                </div>
              );
            }
            return (
              <ZonaVazia key={`v${k}`} style={pos} rotulo={k === grade.idxDestaque ? 'destaque' : 'vazio'}
                ativa={arrastandoAlgo} onSoltar={() => soltar({ pagina: l.pg.id }, k)} />
            );
          })}
        </>
      ) : l.tipo === 'livre' ? (
        <CardsLivres l={l} ctx={ctx} />
      ) : (
        l.blocos.map((b) => {
          // Células livres do bloco: mostram onde ainda cabe produto.
          const ocup = Array.from({ length: b.linhas }, () => Array(b.cols).fill(false));
          b.cards.forEach(c => { for (let i = c.lin; i < c.lin + c.l; i++) for (let j = c.col; j < c.col + c.c; j++) ocup[i][j] = true; });
          const livres = [];
          for (let i = 0; i < b.linhas; i++) for (let j = 0; j < b.cols; j++) if (!ocup[i][j]) livres.push({ i, j });
          return (
            <React.Fragment key={b.id}>
              {/* Bloco vazio: a faixa aparece apagadinha só pra mostrar onde
                  soltar — no PDF ela não sai, senão vira um retângulo vazio. */}
              {b.faixas.map((f, k) => (
                <div key={k} style={{
                  position: 'absolute', left: pct(f.x, A4L), top: pct(f.y, A4A), width: pct(f.w, A4L), height: pct(f.h, A4A),
                  background: f.cor === 'amarela' ? OURO : '#fff', borderRadius: f.cor === 'amarela' ? 4 : 7,
                  zIndex: 0, pointerEvents: 'none', opacity: b.cards.length ? 1 : 0.18,
                }} />
              ))}
              {/* Zona do bloco inteiro: soltar aqui põe no fim do bloco. */}
              <div
                onDragOver={e => { e.preventDefault(); setSobreBloco(b.id); }}
                onDragLeave={() => setSobreBloco(s => (s === b.id ? null : s))}
                onDrop={e => { e.preventDefault(); setSobreBloco(null); soltar({ pagina: l.pg.id, bloco: b.id }, null); }}
                style={{
                  position: 'absolute', left: pct(b.caixa.x, A4L), top: pct(b.caixa.y, A4A),
                  width: pct(b.caixa.w, A4L), height: pct(b.caixa.h, A4A), zIndex: 1,
                  outline: arrastandoAlgo ? `1.5px dashed ${sobreBloco === b.id ? '#fff' : 'rgba(255,255,255,0.35)'}` : 'none',
                  outlineOffset: 2, borderRadius: 4,
                }} />
              {livres.map(({ i, j }) => (
                <div key={`l${i}-${j}`} style={{
                  position: 'absolute', zIndex: 1, pointerEvents: 'none',
                  left: pct(b.caixa.x + j * (b.cardL + G.VAO), A4L),
                  top: pct(b.y + i * (b.alt + G.VAO), A4A),
                  width: pct(b.cardL, A4L), height: pct(b.alt, A4A),
                  border: '1px dashed rgba(255,255,255,0.18)', borderRadius: 6,
                }} />
              ))}
              {b.cards.map((c, k) => (
                <div key={c.p.codpro} style={{
                  position: 'absolute', left: pct(c.x, A4L), top: pct(c.y, A4A),
                  width: pct(c.w, A4L), height: pct(c.h, A4A), zIndex: 2,
                }}>
                  <CardPrevia item={c.p} medidas={c.medidas} solto={b.solto} ctx={ctx}
                    destino={{ pagina: l.pg.id, bloco: b.id }} pos={k} />
                </div>
              ))}
            </React.Fragment>
          );
        })
      )}

      {l.tipo === 'livre' && l.cards.length === 0 && (
        <div className="d-flex align-items-center justify-content-center"
          style={{ position: 'absolute', inset: '35% 10%', color: '#aaa', fontSize: '0.85rem', textAlign: 'center', pointerEvents: 'none' }}>
          Página livre: arraste produtos da lista pra cá (ou clique no +) e mude de lugar e tamanho à vontade.
        </div>
      )}
      {l.blocos && l.blocos.length === 0 && (
        <div className="d-flex align-items-center justify-content-center"
          style={{ position: 'absolute', inset: '30% 10%', color: '#888', fontSize: '0.9rem', textAlign: 'center' }}>
          Página sem blocos — use "+ Bloco" lá em cima.
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Página livre: cada produto é um card solto, igual ao story — clica e arrasta,
// puxa os cantos (o canto oposto fica parado), encosta nas bordas e no meio
// dos outros cards e da página (linha guia laranja). Soltar um produto da
// lista aqui põe ele onde soltou.
// ---------------------------------------------------------------------------
function CardsLivres({ l, ctx }) {
  const { grade, sel, abrir, mudarCaixa, soltar, arrastandoTipo } = ctx;
  const camada = useRef(null);
  const arrasto = useRef(null);
  const [guias, setGuias] = useState([]);
  const [sobre, setSobre] = useState(false);
  const area = areaLivre(grade.geometria, l.n === 0 ? 'capa' : 'interna');

  // Caixa do produto inteiro (como no story): o quadro da foto mais a folha e a
  // fita do "De:", que podem passar dele. É ela que leva o contorno azul, os
  // cantos e as guias — senão o preço "descola" do produto a que se refere.
  const uniao = (card) => {
    const c = card.caixa, p = card.p, fa = folhaAjustada(card.medidas, p.folha, true);
    const fx = c.x + (100 - fa.dir - fa.w) / 100 * c.w, fy = c.y + (100 - fa.baixo - fa.h) / 100 * c.h;
    let x0 = Math.min(c.x, fx), y0 = Math.min(c.y, fy);
    let x1 = Math.max(c.x + c.w, fx + fa.w / 100 * c.w), y1 = Math.max(c.y + c.h, fy + fa.h / 100 * c.h);
    if (p.de > p.por && p.mostrarDe !== false) {
      const ta = tarjaAjustada(p.tarja, true);
      const tx = c.x + ta.x / 100 * c.w, ty = c.y + ta.y / 100 * c.h;   // a fita mede ~metade da folha × 19% dela
      x0 = Math.min(x0, tx); y0 = Math.min(y0, ty);
      x1 = Math.max(x1, tx + fa.w * 0.5 / 100 * c.w); y1 = Math.max(y1, ty + fa.h * 0.19 / 100 * c.h);
    }
    return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
  };

  const apertar = (e, card, modo, canto) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    arrasto.current = { codpro: card.p.codpro, modo, canto, mx: e.clientX, my: e.clientY, c0: card.caixa, u0: uniao(card), moveu: false,
      rect: camada.current.getBoundingClientRect(), outros: l.cards.filter(c => c.p.codpro !== card.p.codpro).map(uniao) };
    abrir(card.p.codpro);
  };

  const mover = (e) => {
    const a = arrasto.current;
    if (!a) return;
    const dxp = e.clientX - a.mx, dyp = e.clientY - a.my;
    if (!a.moveu && Math.hypot(dxp, dyp) < 3) return;   // clique, não arrasto
    a.moveu = true;
    const dx = dxp / a.rect.width * 100, dy = dyp / a.rect.height * 100;
    const tolX = 6 / a.rect.width * 100, tolY = 6 / a.rect.height * 100;
    const alvosX = [area.x0, area.x1, 50, ...a.outros.flatMap(o => [o.x, o.x + o.w, o.x + o.w / 2])];
    const alvosY = [area.y0, area.y1, 50, ...a.outros.flatMap(o => [o.y, o.y + o.h, o.y + o.h / 2])];
    // Encosta o ponto mais perto de um alvo (se passar perto): devolve o ajuste e onde fica a guia.
    const encostar = (pontos, alvos, tol) => {
      let melhor = null;
      pontos.forEach(p => alvos.forEach(t => { const d = t - p; if (Math.abs(d) <= tol && (!melhor || Math.abs(d) < Math.abs(melhor.d))) melhor = { d, t }; }));
      return melhor;
    };
    let c;
    const u = a.u0;
    if (a.modo === 'mover') {
      // Anda o produto inteiro; encosta pela caixa dele (foto + preço).
      const sx = encostar([u.x + dx, u.x + u.w + dx, u.x + u.w / 2 + dx], alvosX, tolX);
      const sy = encostar([u.y + dy, u.y + u.h + dy, u.y + u.h / 2 + dy], alvosY, tolY);
      c = { ...a.c0, x: a.c0.x + dx + (sx?.d || 0), y: a.c0.y + dy + (sy?.d || 0) };
      setGuias([sx && { v: sx.t }, sy && { h: sy.t }].filter(Boolean));
    } else {
      // Igual ao story: o produto inteiro cresce/encolhe mantendo a proporção,
      // com o canto oposto da caixa dele parado (a folha vai junto, é relativa ao quadro).
      const oeste = a.canto.includes('o'), norte = a.canto.includes('n');
      const ax = oeste ? u.x + u.w : u.x, ay = norte ? u.y + u.h : u.y;
      const v0 = [(oeste ? -u.w : u.w) * a.rect.width / 100, (norte ? -u.h : u.h) * a.rect.height / 100];
      const v = [v0[0] + dxp, v0[1] + dyp];
      const k = limitar((v[0] * v0[0] + v[1] * v0[1]) / (v0[0] * v0[0] + v0[1] * v0[1]),
        8 / a.c0.w, Math.min(100 / a.c0.w, 100 / a.c0.h));   // quadro menor que 8% a folha some
      c = { x: ax + (a.c0.x - ax) * k, y: ay + (a.c0.y - ay) * k, w: a.c0.w * k, h: a.c0.h * k };
      setGuias([]);
    }
    mudarCaixa(a.codpro, c);
  };
  const largar = () => { arrasto.current = null; setGuias([]); };

  return (
    <div ref={camada} onPointerMove={mover} onPointerUp={largar} onPointerCancel={largar}
      onPointerDown={e => { if (e.target === camada.current) abrir(null); }}
      onDragOver={e => { if (arrastandoTipo) { e.preventDefault(); setSobre(true); } }}
      onDragLeave={() => setSobre(false)}
      onDrop={e => {
        e.preventDefault(); setSobre(false);
        const r = camada.current.getBoundingClientRect();
        soltar({ pagina: l.pg.id, ponto: { x: (e.clientX - r.left) / r.width * 100, y: (e.clientY - r.top) / r.height * 100 } }, null);
      }}
      style={{ position: 'absolute', inset: 0, zIndex: 1, touchAction: 'none',
        outline: sobre ? '2px dashed rgba(255,255,255,0.7)' : arrastandoTipo ? '1.5px dashed rgba(255,255,255,0.3)' : 'none', outlineOffset: -6 }}>
      {l.cards.map(card => {
        const ativo = sel === card.p.codpro;
        return (
          <div key={card.p.codpro} onPointerDown={e => apertar(e, card, 'mover')}
            style={{ position: 'absolute', left: `${card.caixa.x}%`, top: `${card.caixa.y}%`, width: `${card.caixa.w}%`, height: `${card.caixa.h}%`,
              zIndex: ativo ? 4 : 2, cursor: 'move', touchAction: 'none' }}>
            {/* Todos editáveis direto: clicar no preço já pega o preço (e seleciona o produto). */}
            <CardEditavel item={card.p} medidas={card.medidas} solto={l.pg.estilo === 'soltos'} folha={grade.folha} solta ativo={ativo}
              onAtivar={() => abrir(card.p.codpro)}
              onMudar={(campos) => ctx.mudarItem(card.p.codpro, campos)} onFotoCard={(e) => apertar(e, card, 'mover')} />
          </div>
        );
      })}
      {/* Produto selecionado: contorno azul e cantos na caixa do produto inteiro (foto + preço + "De:"). */}
      {(() => {
        const card = l.cards.find(c => c.p.codpro === sel);
        if (!card) return null;
        const u = uniao(card);
        return <>
          <div style={{ position: 'absolute', left: `${u.x}%`, top: `${u.y}%`, width: `${u.w}%`, height: `${u.h}%`, zIndex: 9,
            outline: '2px dashed #0d6efd', outlineOffset: 3, pointerEvents: 'none' }} />
          {['no', 'ne', 'so', 'se'].map(k => (
            <AlcaCanto key={k} canto={k} x={k.includes('o') ? u.x : u.x + u.w} y={k.includes('n') ? u.y : u.y + u.h}
              onPointerDown={e => apertar(e, card, 'canto', k)} />
          ))}
        </>;
      })()}
      {guias.map((g, i) => (
        <div key={i} style={g.v != null
          ? { position: 'absolute', left: `${g.v}%`, top: 0, bottom: 0, borderLeft: '1px dashed #F16A0D', zIndex: 6, pointerEvents: 'none' }
          : { position: 'absolute', top: `${g.h}%`, left: 0, right: 0, borderTop: '1px dashed #F16A0D', zIndex: 6, pointerEvents: 'none' }} />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Card editado direto na página (o selecionado), como o produto no story:
// a FOLHA do preço e a fita do "De:" arrastam e a folha muda de tamanho pelos
// cantos; a FOTO, na grade, anda dentro do quadro e muda de tamanho pelos
// cantos — na página livre ela leva o card inteiro (`onFotoCard`).
// ---------------------------------------------------------------------------
function CardEditavel({ item, medidas, folha, solto, solta = false, ativo = true, onAtivar, onMudar, onFotoCard, alca }) {
  const palco = useRef(null);
  const arrasto = useRef(null);
  const [sel, setSel] = useState(null);         // 'foto' | 'folha' | 'tarja'
  useEffect(() => { if (!ativo) setSel(null); }, [ativo]);   // outro produto selecionado: some o contorno daqui
  const [nat, setNat] = useState(null);         // altura / largura da foto tratada
  const [tam, setTam] = useState({ l: 120, a: 130 });
  const larg = tam.l, alt = tam.a;
  useEffect(() => {
    if (!palco.current) return undefined;
    const medir = () => setTam({ l: palco.current?.clientWidth || 120, a: palco.current?.clientHeight || 130 });
    medir();
    const obs = new ResizeObserver(medir);
    obs.observe(palco.current);
    return () => obs.disconnect();
  }, []);
  useEffect(() => {
    if (!item.foto.url) { setNat(null); return undefined; }
    let vivo = true;
    carregarImagem(item.foto.url).then(img => { if (vivo) setNat(img.naturalHeight / img.naturalWidth); }).catch(() => {});
    return () => { vivo = false; };
  }, [item.foto.url]);

  // ---- Onde está cada peça, em % do card
  // Foto: o que aparece dela (recuo + "contain"), depois do arrasto e do zoom.
  // O recuo do CSS é em % da LARGURA nos quatro lados (padding em %).
  const caixaFoto = () => {
    let x0 = 0, y0 = 0, x1 = 100, y1 = 100;
    if (!item.foto.cobre) {
      const pl = 0.07 * larg, pr = medidas.padDir / 100 * larg, pb = medidas.padBaixo / 100 * larg;
      const bw = larg - pl - pr, bh = alt - pl - pb;
      let iw = bw, ih = bh;
      if (nat) { if (bw * nat > bh) { ih = bh; iw = bh / nat; } else { ih = bw * nat; } }
      const ix = pl + (bw - iw) / 2, iy = pl + (bh - ih) / 2;
      x0 = ix / larg * 100; y0 = iy / alt * 100; x1 = (ix + iw) / larg * 100; y1 = (iy + ih) / alt * 100;
    }
    const { zoom = 1, x = 0, y = 0 } = item.foto;
    return { x: 50 + zoom * (x0 - 50) + x, y: 50 + zoom * (y0 - 50) + y, w: (x1 - x0) * zoom, h: (y1 - y0) * zoom, x0, y0, x1, y1 };
  };
  const fa = folhaAjustada(medidas, item.folha, solta);
  const caixaFolha = { x: 100 - fa.dir - fa.w, y: 100 - fa.baixo - fa.h, w: fa.w, h: fa.h };
  // Folha a partir de escala + posição (dir/baixo em % do card), limitada e no formato salvo.
  const folhaEm = (e, dir, baixo) => {
    const f = medidas.folha;
    const n = folhaAjustada(medidas, { e, dx: f.dir - dir, dy: f.baixo - baixo }, solta);
    return { e: Math.round(n.e * 1000) / 1000, dx: r1(f.dir - n.dir), dy: r1(f.baixo - n.baixo) };
  };

  const apertar = (e, alvo) => {
    const tipo = typeof alvo === 'string' ? alvo : alvo.tipo;
    // Página livre, igual ao story: a foto leva o produto inteiro. Clicar no preço
    // (ou no "De:") já pega ele — arrasta e os cantos dele mudam o tamanho.
    if (onFotoCard && tipo === 'foto') { setSel(null); onFotoCard(e); return; }
    if (e.button !== 0) return;
    onAtivar?.();
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    const rect = palco.current.getBoundingClientRect();
    const a = { tipo, mx: e.clientX, my: e.clientY, foto: { ...item.foto }, fa, ta: tarjaAjustada(item.tarja, solta) };
    // Puxando um canto, o canto oposto fica parado (âncora em % do card).
    if (tipo === 'foto-escala') {
      const c = caixaFoto();
      const oeste = alvo.canto.includes('o'), norte = alvo.canto.includes('n');
      a.orig = [oeste ? c.x1 : c.x0, norte ? c.y1 : c.y0];             // canto oposto, antes do zoom
      a.ancora = [oeste ? c.x + c.w : c.x, norte ? c.y + c.h : c.y];    // e onde ele está na tela
    } else if (tipo === 'folha-escala') {
      a.caixa = caixaFolha;
      a.ancora = [alvo.canto.includes('o') ? caixaFolha.x + caixaFolha.w : caixaFolha.x, alvo.canto.includes('n') ? caixaFolha.y + caixaFolha.h : caixaFolha.y];
    }
    a.ancoraPx = a.ancora && [rect.left + a.ancora[0] / 100 * larg, rect.top + a.ancora[1] / 100 * alt];
    arrasto.current = a;
    setSel(tipo.replace('-escala', ''));
  };

  const mover = (e) => {
    const a = arrasto.current;
    if (!a) return;
    const dx = (e.clientX - a.mx) / larg * 100, dy = (e.clientY - a.my) / alt * 100;
    // Quanto o ponteiro se afastou da âncora, comparado ao começo do arrasto.
    const escala = () => {
      const v0 = [a.mx - a.ancoraPx[0], a.my - a.ancoraPx[1]], v = [e.clientX - a.ancoraPx[0], e.clientY - a.ancoraPx[1]];
      const n0 = v0[0] * v0[0] + v0[1] * v0[1];
      return n0 ? Math.max(0.05, (v[0] * v0[0] + v[1] * v0[1]) / n0) : 1;
    };
    if (a.tipo === 'foto') {
      onMudar(it => ({ foto: { ...it.foto, x: r1(limitar(a.foto.x + dx, -150, 150)), y: r1(limitar(a.foto.y + dy, -150, 150)) } }));
    } else if (a.tipo === 'foto-escala') {
      const z = limitar((a.foto.zoom || 1) * escala(), 0.3, 3);
      const x = a.ancora[0] - 50 - z * (a.orig[0] - 50), y = a.ancora[1] - 50 - z * (a.orig[1] - 50);
      onMudar(it => ({ foto: { ...it.foto, zoom: Math.round(z * 100) / 100, x: r1(x), y: r1(y) } }));
    } else if (a.tipo === 'folha') {
      onMudar({ folha: folhaEm(a.fa.e, a.fa.dir - dx, a.fa.baixo - dy) });
    } else if (a.tipo === 'folha-escala') {
      const e1 = folhaAjustada(medidas, { e: a.fa.e * escala() }, solta).e;
      const k = e1 / a.fa.e, c = a.caixa, [ax, ay] = a.ancora;
      const x = ax + (c.x - ax) * k, y = ay + (c.y - ay) * k;
      onMudar({ folha: folhaEm(e1, 100 - x - c.w * k, 100 - y - c.h * k) });
    } else if (a.tipo === 'tarja') {
      const n = tarjaAjustada({ dx: a.ta.x - 4.5 + dx, dy: a.ta.y - 4.5 + dy }, solta);
      onMudar({ tarja: { dx: r1(n.x - 4.5), dy: r1(n.y - 4.5) } });
    }
  };
  const largar = () => { arrasto.current = null; };

  const cantos = (c, tipo) => [['no', c.x, c.y], ['ne', c.x + c.w, c.y], ['so', c.x, c.y + c.h], ['se', c.x + c.w, c.y + c.h]]
    .map(([canto, x, y]) => <AlcaCanto key={canto} canto={canto} x={x} y={y} onPointerDown={(e) => apertar(e, { tipo, canto })} />);
  const contorno = (c, cor = '#F16A0D') => (
    <div style={{ position: 'absolute', left: `${c.x}%`, top: `${c.y}%`, width: `${c.w}%`, height: `${c.h}%`,
      outline: `2px dashed ${cor}`, outlineOffset: 1, pointerEvents: 'none', zIndex: 20 }} />
  );

  return (
    <div ref={palco} onPointerMove={mover} onPointerUp={largar} onPointerCancel={largar}
      style={{ position: 'absolute', inset: 0, touchAction: 'none' }}>
      <div style={{ position: 'absolute', inset: 0, overflow: solta ? 'visible' : 'hidden', borderRadius: 7, userSelect: 'none',
        border: solto ? '1.5px solid transparent' : '1.5px solid #F7A233', background: solto ? 'transparent' : '#0e0e0e' }}>
        <Moldura item={item} medidas={medidas} folha={folha} largura={larg} altura={alt} onApertar={apertar} sel={ativo ? sel : null} solta={solta} />
      </div>
      {ativo && sel === 'foto' && !onFotoCard && item.foto.url && <>{contorno(caixaFoto())}{cantos(caixaFoto(), 'foto-escala')}</>}
      {ativo && sel === 'folha' && <>{contorno(caixaFolha)}{cantos(caixaFolha, 'folha-escala')}</>}
      {ativo && onFotoCard && (
        <div onPointerDown={(e) => apertar(e, { tipo: 'folha', direto: true })} title="Arraste pra mudar só o preço de lugar"
          style={{ position: 'absolute', zIndex: 32, left: `${caixaFolha.x + caixaFolha.w / 2}%`, top: `${caixaFolha.y + caixaFolha.h}%`,
            transform: 'translate(-50%, 4px)', background: '#F16A0D', color: '#fff', fontSize: 11, fontWeight: 600, borderRadius: 10,
            padding: '1px 8px', cursor: 'move', whiteSpace: 'nowrap', touchAction: 'none', boxShadow: '0 1px 4px rgba(0,0,0,0.4)' }}>
          <i className="bi bi-arrows-move me-1"></i>preço
        </div>
      )}
      {alca}
    </div>
  );
}

function ZonaVazia({ style, rotulo, ativa, onSoltar }) {
  const [sobre, setSobre] = useState(false);
  return (
    <div
      onDragOver={e => { e.preventDefault(); setSobre(true); }}
      onDragLeave={() => setSobre(false)}
      onDrop={e => { e.preventDefault(); setSobre(false); onSoltar(); }}
      style={{
        position: 'absolute', ...style, zIndex: 1,
        border: `1px dashed ${sobre ? '#fff' : ativa ? '#999' : '#555'}`, borderRadius: 7,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: sobre ? '#fff' : '#666', fontSize: '0.65rem',
        background: sobre ? 'rgba(255,255,255,0.08)' : 'transparent',
      }}>{rotulo}</div>
  );
}

// ---------------------------------------------------------------------------
// Card na prévia: arrastável, e quem soltar em cima dele entra no lugar dele.
// ---------------------------------------------------------------------------
function CardPrevia({ item, medidas, solto, ctx, destino, pos }) {
  const [sobre, setSobre] = useState(false);
  const ref = useRef(null);
  const [tam, setTam] = useState({ l: 120, a: 130 });
  const selecionado = ctx.sel === item.codpro;
  useEffect(() => {
    if (!ref.current) return;
    const medir = () => setTam({ l: ref.current?.clientWidth || 120, a: ref.current?.clientHeight || 130 });
    medir();
    const obs = new ResizeObserver(medir);
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  const comecar = e => { e.dataTransfer.setData('text/plain', item.codpro); ctx.comecarArrasto({ tipo: 'item', codpro: item.codpro }); };
  return (
    <div ref={ref}
      // Selecionado, o card é editado ali mesmo (foto, preço, "De:"); pra trocar de lugar, a alça ⠿.
      draggable={!selecionado}
      onDragStart={comecar}
      onDragEnd={ctx.terminarArrasto}
      onDragOver={e => { e.preventDefault(); e.stopPropagation(); setSobre(true); }}
      onDragLeave={() => setSobre(false)}
      onDrop={e => { e.preventDefault(); e.stopPropagation(); setSobre(false); ctx.soltar(destino, pos); }}
      onClick={() => ctx.abrir(item.codpro)}
      title={selecionado ? undefined : 'Clique pra ajustar · arraste pra mudar de lugar'}
      style={{
        position: 'absolute', inset: 0, cursor: 'pointer', overflow: selecionado ? 'visible' : 'hidden',
        border: selecionado ? 'none' : solto ? `1.5px dashed ${sobre ? '#fff' : 'transparent'}` : `1.5px solid ${sobre ? '#fff' : '#F7A233'}`,
        borderRadius: 7,
        background: solto || selecionado ? 'transparent' : '#0e0e0e',
        boxShadow: sobre ? '-4px 0 0 0 #fff' : selecionado ? '0 0 0 3px #0d6efd' : 'none',
        zIndex: selecionado ? 5 : undefined,
      }}>
      {selecionado
        ? <CardEditavel item={item} medidas={medidas} folha={ctx.grade.folha} solto={solto}
            onMudar={(campos) => ctx.mudarItem(item.codpro, campos)}
            alca={(
              <div draggable onDragStart={comecar} onDragEnd={ctx.terminarArrasto} title="Arraste pra mudar de lugar"
                style={{ position: 'absolute', left: -2, top: -24, zIndex: 31, background: '#0d6efd', color: '#fff', borderRadius: 5,
                  fontSize: 11, padding: '1px 7px', cursor: 'grab', whiteSpace: 'nowrap' }}>
                <i className="bi bi-grip-vertical"></i> mover
              </div>
            )} />
        : <Moldura item={item} medidas={medidas} folha={ctx.grade.folha} largura={tam.l} altura={tam.a} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Desenho do card: foto, "De:" e a folha do preço com as MESMAS medidas do PDF
// (a folha é o próprio contorno vetorial, e textos e tamanhos vêm de lá).
// ---------------------------------------------------------------------------
function Moldura({ item, medidas, folha, largura, altura, onApertar, sel, solta = false }) {
  // Folha e fita do "De:" onde a gerente deixou (sem ajuste = padrão), igual ao PDF.
  // `solta` (página livre): elas podem passar da caixa do produto.
  const fa = folhaAjustada(medidas, item.folha, solta);
  const ta = tarjaAjustada(item.tarja, solta);
  const alturaFolha = altura * fa.h / 100;
  // No editor (onApertar), cada peça pode ser agarrada; no resto, nada muda.
  const ap = (alvo) => (onApertar ? (e) => onApertar(e, alvo) : undefined);
  const mao = onApertar ? 'move' : undefined;
  const marcado = (alvo) => (sel === alvo ? { outline: '2px dashed #0d6efd', outlineOffset: 2 } : {});
  return (
    <>
      {item.foto.carregando && (
        <div className="d-flex align-items-center justify-content-center h-100 text-muted">
          <span className="spinner-border spinner-border-sm"></span>
        </div>
      )}
      {!item.foto.carregando && item.foto.url && (
        // A foto fica sempre dentro do quadro (o PDF faz igual: .foto com overflow).
        <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', borderRadius: 7 }}>
          <img src={item.foto.url} alt="" draggable={false} onPointerDown={ap('foto')} style={{
            position: 'absolute', inset: 0, width: '100%', height: '100%',
            objectFit: item.foto.cobre ? 'cover' : 'contain',
            padding: item.foto.cobre ? 0 : `7% ${medidas.padDir}% ${medidas.padBaixo}% 7%`,
            transform: `translate(${item.foto.x}%, ${item.foto.y}%) scale(${item.foto.zoom})`, cursor: mao,
          }} />
        </div>
      )}
      {!item.foto.carregando && !item.foto.url && (
        <div className="d-flex align-items-start justify-content-center h-100 pt-3"
          style={{ color: '#b23b3b', fontSize: Math.max(7, largura * 0.06) }}>sem foto</div>
      )}
      {item.de > item.por && item.mostrarDe !== false && (
        <span onPointerDown={ap('tarja')} style={{
          position: 'absolute', top: `${ta.y}%`, left: `${ta.x}%`, zIndex: 3, background: '#000', color: '#DF5200',
          fontStyle: 'italic', fontWeight: 700, fontSize: Math.max(5, alturaFolha * 0.138), lineHeight: 1,
          padding: '0.16em 0.9em 0.2em', borderRadius: 2, whiteSpace: 'nowrap', cursor: mao, ...marcado('tarja'),
        }}>De: {fmt(item.de)}</span>
      )}
      <FolhaPreco item={item} f={fa} folha={folha} alturaFolha={alturaFolha} onPointerDown={ap('folha')} cursor={mao} />
    </>
  );
}

function FolhaPreco({ item, f, folha, alturaFolha, onPointerDown, cursor }) {
  const { T, TOPO, INT_ESQ } = folha;
  const un = UNIDADES[String(item.unidade || '').toUpperCase()];
  const [intBruto, cent] = Number(item.por || 0).toFixed(2).split('.');
  const int = milhar(intBruto);
  const caixa = useRef(null);
  const numRef = useRef(null);
  // Igual ao PDF: os centavos entram logo depois do número (medido), e se o
  // preço for comprido o número encolhe pra não estourar a folha.
  const [ajuste, setAjuste] = useState({ fim: INT_ESQ + 38, escala: 1 });
  const escalaAtual = useRef(1);
  useLayoutEffect(() => {
    const c = caixa.current, nEl = numRef.current;
    if (!c || !nEl || !c.clientWidth) return;
    const larg = c.clientWidth;
    // O número na tela já pode estar encolhido: volta pra largura cheia antes de
    // calcular, senão a escala sai errada quando o card muda de tamanho.
    const largCheia = ((nEl.offsetLeft + nEl.offsetWidth) / larg * 100 - INT_ESQ) / escalaAtual.current;
    const largSufixo = 19;   // ",90" + "à vista" ocupam ~19% da folha
    let escala = 1;
    if (INT_ESQ + largCheia + largSufixo > 87) escala = Math.max(0.25, (87 - largSufixo - INT_ESQ) / largCheia);
    const fim = INT_ESQ + largCheia * escala;
    escalaAtual.current = escala;
    setAjuste(a => (Math.abs(a.fim - fim) > 0.3 || a.escala !== escala ? { fim, escala } : a));
  }, [int, alturaFolha, INT_ESQ]);

  const abs = { position: 'absolute', lineHeight: 1, whiteSpace: 'nowrap' };
  const ouro = { color: OURO, fontWeight: 800 };
  return (
    <div ref={caixa} onPointerDown={onPointerDown} style={{
      position: 'absolute', width: `${f.w}%`, height: `${f.h}%`, right: `${f.dir}%`, bottom: `${f.baixo}%`,
      background: `url(${folha.svg}) no-repeat center/100% 100%`, fontSize: alturaFolha, zIndex: 2, cursor,
    }}>
      <span style={{ ...abs, left: '8%', right: '8%', top: `${TOPO.cod}%`, textAlign: 'center', color: '#fff', fontSize: `${T.cod}em` }}>
        Cód.{item.codpro}
      </span>
      <span style={{ position: 'absolute', left: '17%', right: '13%', top: `${TOPO.nome}%`, textAlign: 'center',
        color: '#FAC046', fontStyle: 'italic', fontSize: `${T.nome}em`, lineHeight: 1,
        display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
        {item.nome}
      </span>
      <span style={{ ...abs, ...ouro, right: '70.1%', top: `${TOPO.por}%`, fontSize: `${T.por}em` }}>Por:</span>
      <span style={{ ...abs, ...ouro, right: '70.6%', top: `${TOPO.rs}%`, fontSize: `${T.rs}em` }}>R$</span>
      {/* Encolhido, o número fica centrado na mesma altura (igual ao PDF). */}
      <span ref={numRef} style={{ ...abs, ...ouro, left: `${INT_ESQ}%`,
        top: `${TOPO.int + 50 * T.int * (1 - ajuste.escala)}%`, fontSize: `${T.int * ajuste.escala}em` }}>{int}</span>
      <span style={{ ...abs, ...ouro, left: `${ajuste.fim - 0.5}%`, top: `${TOPO.cent}%`, fontSize: `${T.cent}em` }}>,{cent}</span>
      {un && <span style={{ ...abs, ...ouro, fontWeight: 700, left: `${ajuste.fim + 4.4}%`, top: `${TOPO.un}%`, fontSize: `${T.un}em` }}>{un}</span>}
      <span style={{ ...abs, ...ouro, fontWeight: 700, left: `${ajuste.fim - 0.3}%`, top: `${TOPO.avista}%`, fontSize: `${T.avista}em` }}>à vista</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Editor de um produto: texto, foto, tamanho do card e, direto na prévia, onde
// ficam a foto, a folha do preço e a fita do "De:" — arrasta e puxa os cantos,
// igual ao story. A prévia é o card no tamanho e na proporção reais.
// ---------------------------------------------------------------------------
const r1 = (v) => Math.round(v * 10) / 10;
const limitar = (v, min, max) => Math.min(max, Math.max(min, v));
const CURSOR_CANTO = { no: 'nwse-resize', se: 'nwse-resize', ne: 'nesw-resize', so: 'nesw-resize' };

function AlcaCanto({ canto, x, y, onPointerDown }) {
  return (
    <div onPointerDown={onPointerDown}
      style={{ position: 'absolute', left: `${x}%`, top: `${y}%`, transform: 'translate(-50%, -50%)', width: 12, height: 12,
        background: '#fff', border: '2px solid #0d6efd', borderRadius: 3, cursor: CURSOR_CANTO[canto], zIndex: 30, touchAction: 'none' }} />
  );
}

function AjustesItem({ item, semTamanho, medidas, campanhaId, livre, onFechar, onMudar, onFoto, onRemover, onCaixa, onOrdem }) {
  const inputFoto = useRef(null);
  const fa = medidas ? folhaAjustada(medidas, item.folha, !!livre) : null;

  const trocarFoto = async (file) => {
    if (!file) return;
    try {
      // Reduz antes de subir: foto de celular passava do limite de envio.
      const { mime, dados } = await reduzirParaEnvio(file);
      await axios.post(`${API}/catalogo/campanha/${campanhaId}/foto/${item.codpro}`, { mime, dados });
      onFoto({ origem: 'propria', bruta: null });
    } catch (e) {
      Swal.fire('Erro', e.response?.data?.error || e.message || 'Não consegui trocar a foto.', 'error');
    } finally {
      if (inputFoto.current) inputFoto.current.value = '';
    }
  };

  const voltarErp = async () => {
    try { await axios.delete(`${API}/catalogo/campanha/${campanhaId}/foto/${item.codpro}`); } catch { /* já não existia */ }
    onFoto({ origem: 'erp', bruta: null });
  };

  const tamAtual = tamanhoDe(item);
  const rotulo = { fontSize: '0.82rem', fontWeight: 600 };

  // Página livre: o card inteiro cresce/encolhe em volta do centro, mantendo a proporção.
  const tamanhoCard = (w) => {
    const c = livre.caixa, k = w / c.w;
    onCaixa({ x: c.x + c.w / 2 - (c.w * k) / 2, y: c.y + c.h / 2 - (c.h * k) / 2, w: c.w * k, h: c.h * k });
  };
  const deslizar = (titulo, valor, texto, min, max, passo, fn) => (
    <>
      <label className="form-label mb-0" style={rotulo}>{titulo} <span className="text-muted">({texto})</span></label>
      <input type="range" className="form-range" min={min} max={max} step={passo} value={limitar(valor, min, max)}
        onChange={e => fn(Number(e.target.value))} />
    </>
  );

  return (
    <div>
      <div className="d-flex justify-content-between align-items-start">
        <div style={{ minWidth: 0 }}>
          <div className="fw-bold" style={{ fontSize: '0.9rem' }}>Cód.{item.codpro}</div>
          <small className="text-muted d-block mb-1">De R$ {fmt(item.de)} · Por R$ {fmt(item.por)}</small>
        </div>
        <button className="btn btn-sm btn-link text-secondary p-0" title="Fechar (Esc)" onClick={onFechar}><i className="bi bi-x-lg"></i></button>
      </div>
      <small className="text-muted d-block mb-2" style={{ fontSize: '0.72rem' }}>
        <i className="bi bi-hand-index me-1"></i>Direto na página: {livre
          ? 'arraste o produto e puxe os cantos; a pílula "preço" muda só o preço de lugar (e aí os cantos dele mudam o tamanho); o "De:" arrasta · setas ajustam fino.'
          : 'arraste a foto dentro do quadro (os cantos dela mudam o tamanho), a folha do preço e o "De:"; pra trocar o card de lugar, a alça "mover".'}
      </small>

      <label className="form-label mb-1" style={rotulo}>Texto do produto</label>
      <textarea className="form-control form-control-sm mb-2" rows={2} value={item.nome}
        onChange={e => onMudar({ nome: e.target.value })} />

      {livre
        ? deslizar('Tamanho do produto', livre.caixa.w, `${Math.round(livre.caixa.w)}% da página`, 8, 100, 0.5, tamanhoCard)
        : <>
            {/* Tamanho que não dá aqui fica desligado, com o motivo embaixo. */}
            <label className="form-label mb-1" style={rotulo}>Tamanho do card</label>
            <div className="d-flex gap-1 flex-wrap mb-1">
              {TAMANHOS_CARD.map(t => (
                <button key={t.valor} type="button" title={semTamanho[t.valor] || t.ajuda}
                  className={`btn btn-sm ${tamAtual === t.valor ? 'btn-warning' : 'btn-outline-secondary'}`}
                  disabled={!!semTamanho[t.valor] && tamAtual !== t.valor}
                  onClick={() => onMudar({ tamanho: t.valor, grande: t.valor === 'grande' })}>{t.rotulo}</button>
              ))}
            </div>
            {semTamanho[tamAtual] && (
              <div className="text-muted mb-1" style={{ fontSize: '0.72rem' }}>Aqui ele aparece no tamanho normal. {semTamanho[tamAtual]}</div>
            )}
            <small className="text-muted d-block mb-2" style={{ fontSize: '0.68rem' }}>Se não houver espaço no bloco, ele aparece em "não couberam".</small>
          </>}

      {deslizar('Só a foto', (item.foto.zoom || 1) * 100, `${Math.round((item.foto.zoom || 1) * 100)}%`, 30, 300, 5,
        (v) => onMudar(it => ({ foto: { ...it.foto, zoom: v / 100 } })))}
      {deslizar('Só o preço', (fa?.e || 1) * 100, `${Math.round((fa?.e || 1) * 100)}%`, 50, 130, 1,
        (v) => onMudar(it => ({ folha: { dx: 0, dy: 0, ...it.folha, e: v / 100 } })))}

      <div className="form-check mb-2">
        <input className="form-check-input" type="checkbox" id={`de-${item.codpro}`} checked={item.mostrarDe !== false}
          disabled={!(item.de > item.por)} onChange={e => onMudar({ mostrarDe: e.target.checked })} />
        <label className="form-check-label" htmlFor={`de-${item.codpro}`} style={{ fontSize: '0.82rem' }}>
          Mostrar "De: R$ {fmt(item.de)}"
        </label>
      </div>

      <label className="form-label mb-1" style={rotulo}>Estilo da foto</label>
      <div className="d-flex gap-1 flex-wrap mb-2">
        {ESTILOS.map(e => (
          <button key={e.valor} type="button"
            className={`btn btn-sm ${item.foto.estilo === e.valor ? 'btn-warning' : 'btn-outline-secondary'}`}
            onClick={() => onFoto({ estilo: e.valor, tocouEstilo: true })}>
            {e.rotulo}
          </button>
        ))}
      </div>

      <div className="d-flex gap-1 flex-wrap mb-2">
        <button className="btn btn-sm btn-outline-secondary" onClick={() => onMudar(it => ({ foto: { ...it.foto, zoom: 1, x: 0, y: 0 } }))}>
          Centralizar foto
        </button>
        <button className="btn btn-sm btn-outline-secondary" disabled={!item.folha} onClick={() => onMudar({ folha: null })}>
          Preço no lugar
        </button>
        {item.de > item.por && item.mostrarDe !== false && (
          <button className="btn btn-sm btn-outline-secondary" disabled={!item.tarja} onClick={() => onMudar({ tarja: null })}>
            "De:" no lugar
          </button>
        )}
      </div>

      <div className="d-flex gap-2 flex-wrap mb-2">
        <input ref={inputFoto} type="file" accept="image/jpeg,image/png,image/webp" hidden
          onChange={e => trocarFoto(e.target.files?.[0])} />
        <button className="btn btn-sm btn-outline-primary" onClick={() => inputFoto.current?.click()}>
          <i className="bi bi-upload me-1"></i>Trocar foto
        </button>
        {item.foto.origem === 'propria' && (
          <button className="btn btn-sm btn-outline-secondary" onClick={voltarErp}>
            <i className="bi bi-arrow-counterclockwise me-1"></i>Voltar à do sistema
          </button>
        )}
      </div>
      <AvisoSugestaoFoto sugestao={item.sugestao} />

      {livre && (
        <div className="d-flex gap-1 mb-2">
          <button className="btn btn-sm btn-outline-secondary flex-fill" onClick={() => onOrdem(true)} title="Desenhar por cima dos outros">
            <i className="bi bi-front me-1"></i>Pra frente
          </button>
          <button className="btn btn-sm btn-outline-secondary flex-fill" onClick={() => onOrdem(false)} title="Desenhar por baixo dos outros">
            <i className="bi bi-back me-1"></i>Pra trás
          </button>
        </div>
      )}
      <button className="btn btn-sm btn-outline-danger" onClick={onRemover}>
        <i className="bi bi-trash me-1"></i>Tirar do catálogo
      </button>
    </div>
  );
}
